import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { dataService, Registration, School, AppSchool, Region, SchoolLevel } from '../services/dataService';
import { User } from '../services/authService';
import { UserAccountManager, DashboardHeader } from '../components';
import '../styles/dashboard-redesign.css';
import '../styles/dashboard-header.css';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';

interface AdminStats {
  totalSchools: number;
  totalUsers: number;
  totalRegistrations: number;
  pendingRegistrations: number;
  approvedRegistrations: number;
  rejectedRegistrations: number;
  totalStudents: number;
  approvedStudents: number;
  activeCoaches: number;
  activeHeadteachers: number;
  weeklyRegistrations: number;
}

interface SchoolStats {
  school: School;
  registrations: number;
  coaches: number;
  students: number;
}

export type AdminDashboardTab = 'dashboard' | 'users' | 'registrations' | 'schools' | 'account';

interface AdminDashboardProps {
  activeTab?: AdminDashboardTab;
}

// Helper function to calculate average weekly registrations
const calculateWeeklyRegistrations = (registrations: any[]): number => {
  const now = new Date();
  const fourWeeksAgo = new Date(now.getTime() - (4 * 7 * 24 * 60 * 60 * 1000)); // 4 weeks ago
  
  const recentRegistrations = registrations.filter(reg => {
    const regDate = new Date(reg.created_at || reg.registrationDate);
    return regDate >= fourWeeksAgo;
  });
  
  return Math.round(recentRegistrations.length / 4); // Average per week over 4 weeks
};

// Age group helper functions
const getAgeAtEndOfYear = (dateOfBirth: string): number => {
  const currentYear = new Date().getFullYear();
  const birthYear = new Date(dateOfBirth).getFullYear();
  return currentYear - birthYear;
};

const getAgeGroup = (dateOfBirth: string): string => {
  const age = getAgeAtEndOfYear(dateOfBirth);
  
  if (age >= 10 && age <= 12) {
    return 'Grade 7';
  } else if (age >= 13 && age <= 15) {
    return 'Under 15';
  } else if (age >= 16 && age <= 17) {
    return 'Under 17';
  } else if (age >= 18 && age <= 20) {
    return 'Under 20';
  } else {
    // Fallback for ages outside our normal ranges
    if (age < 10) return 'Grade 7';
    if (age > 20) return 'Under 20';
    return 'Under 15'; // Default fallback
  }
};

// Calculate age group distribution from unique students (not total registrations)
const calculateAgeGroupDistribution = (registrations: Registration[]) => {
  const ageGroups = {
    'Grade 7': 0,
    'Under 15': 0,
    'Under 17': 0,
    'Under 20': 0
  };

  // Create a Set to track unique students based on their identifying information
  const uniqueStudents = new Set<string>();
  const studentAgeGroups: { [key: string]: string } = {};

  registrations.forEach(registration => {
    const dateOfBirth = registration.date_of_birth || registration.dateOfBirth;
    const firstName = registration.first_name || registration.firstName;
    const lastName = registration.last_name || registration.lastName;
    const gender = registration.gender;
    
    if (dateOfBirth && firstName && lastName && gender) {
      // Create a unique identifier for each student
      const studentId = `${firstName.toLowerCase().trim()}_${lastName.toLowerCase().trim()}_${dateOfBirth}_${gender.toLowerCase()}`;
      
      if (!uniqueStudents.has(studentId)) {
        uniqueStudents.add(studentId);
        const ageGroup = getAgeGroup(dateOfBirth);
        studentAgeGroups[studentId] = ageGroup;
        ageGroups[ageGroup as keyof typeof ageGroups]++;
      }
    }
  });

  return [
    { name: 'Grade 7', value: ageGroups['Grade 7'], color: '#FFD700' },
    { name: 'Under 15', value: ageGroups['Under 15'], color: '#10b981' },
    { name: 'Under 17', value: ageGroups['Under 17'], color: '#C41E3A' },
    { name: 'Under 20', value: ageGroups['Under 20'], color: '#3b82f6' },
  ].filter(group => group.value > 0); // Only show groups with data
};

// Calculate approval statistics based on unique students
const calculateApprovalStats = (registrations: Registration[]) => {
  const uniqueStudents = new Set<string>();
  const approvedStudents = new Set<string>();
  const pendingStudents = new Set<string>();
  const rejectedStudents = new Set<string>();

  registrations.forEach(reg => {
    const firstName = reg.first_name || reg.firstName;
    const lastName = reg.last_name || reg.lastName;
    const dateOfBirth = reg.date_of_birth || reg.dateOfBirth;
    const gender = reg.gender;
    
    if (firstName && lastName && dateOfBirth && gender) {
      const studentId = `${firstName.toLowerCase().trim()}_${lastName.toLowerCase().trim()}_${dateOfBirth}_${gender.toLowerCase()}`;
      uniqueStudents.add(studentId);

      if (reg.status === 'approved') {
        approvedStudents.add(studentId);
      } else if (reg.status === 'pending') {
        pendingStudents.add(studentId);
      } else if (reg.status === 'rejected') {
        rejectedStudents.add(studentId);
      }
    }
  });

  const totalStudents = uniqueStudents.size;
  const approvalRate = totalStudents > 0 ? Math.round((approvedStudents.size / totalStudents) * 100) : 0;

  return {
    totalStudents,
    approvedStudents: approvedStudents.size,
    pendingStudents: pendingStudents.size,
    rejectedStudents: rejectedStudents.size,
    approvalRate
  };
};

// Calculate weekly registration trends from actual data
const calculateRegistrationTrends = (registrations: Registration[]) => {
  if (registrations.length === 0) {
    return [];
  }

  // Find the oldest registration date to start Week 1
  const validRegistrations = registrations.filter(reg => {
    const dateString = reg.created_at || reg.registrationDate;
    return dateString && !isNaN(new Date(dateString).getTime());
  });

  if (validRegistrations.length === 0) {
    return [];
  }

  const oldestDate = new Date(Math.min(
    ...validRegistrations.map(reg => new Date(reg.created_at || reg.registrationDate || '').getTime())
  ));

  // Set oldest date to start of that week (Sunday)
  const firstWeekStart = new Date(oldestDate);
  firstWeekStart.setDate(oldestDate.getDate() - oldestDate.getDay());
  firstWeekStart.setHours(0, 0, 0, 0);

  // Calculate how many weeks have passed since the first registration
  const now = new Date();
  const currentWeekStart = new Date(now);
  currentWeekStart.setDate(now.getDate() - now.getDay());
  currentWeekStart.setHours(0, 0, 0, 0);

  const weeksDifference = Math.floor((currentWeekStart.getTime() - firstWeekStart.getTime()) / (7 * 24 * 60 * 60 * 1000));
  const totalWeeks = weeksDifference + 1;

  // Limit to maximum 12 weeks to keep chart readable
  const startWeek = totalWeeks > 12 ? totalWeeks - 12 : 0;

  // Generate weeks from first registration (or recent 12 weeks) to current week
  const weeks = [];
  for (let i = startWeek; i < totalWeeks; i++) {
    const weekStart = new Date(firstWeekStart);
    weekStart.setDate(firstWeekStart.getDate() + (i * 7));
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    
    weeks.push({
      start: weekStart,
      end: weekEnd,
      label: `Week ${i + 1}`
    });
  }
  
  return weeks.map(week => {
    // Get registrations for this week
    const weekRegistrations = registrations.filter(reg => {
      const dateString = reg.created_at || reg.registrationDate;
      if (!dateString) return false;
      
      const regDate = new Date(dateString);
      return regDate >= week.start && regDate <= week.end;
    });
    
    // Count unique students for registrations (not total registrations)
    const uniqueStudentsRegistered = new Set<string>();
    const uniqueStudentsApproved = new Set<string>();
    const uniqueStudentsRejected = new Set<string>();
    
    weekRegistrations.forEach(reg => {
      const firstName = reg.first_name || reg.firstName;
      const lastName = reg.last_name || reg.lastName;
      const dateOfBirth = reg.date_of_birth || reg.dateOfBirth;
      const gender = reg.gender;
      
      if (firstName && lastName && dateOfBirth && gender) {
        const studentId = `${firstName.toLowerCase().trim()}_${lastName.toLowerCase().trim()}_${dateOfBirth}_${gender.toLowerCase()}`;
        
        uniqueStudentsRegistered.add(studentId);
        
        if (reg.status === 'approved') {
          uniqueStudentsApproved.add(studentId);
        } else if (reg.status === 'rejected') {
          uniqueStudentsRejected.add(studentId);
        }
      }
    });
    
    return {
      week: week.label,
      registrations: uniqueStudentsRegistered.size,
      approvals: uniqueStudentsApproved.size,
      rejections: uniqueStudentsRejected.size
    };
  });
};

const AdminDashboard: React.FC<AdminDashboardProps> = ({ activeTab: propActiveTab = 'dashboard' }) => {
  const { user, isLoading, isRegistrationOpen, toggleRegistrationStatus } = useApp();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminDashboardTab>(propActiveTab);

  const handleReports = () => {
    navigate('/reports');
  };
  const [stats, setStats] = useState<AdminStats>({
    totalSchools: 0,
    totalUsers: 0,
    totalRegistrations: 0,
    pendingRegistrations: 0,
    approvedRegistrations: 0,
    rejectedRegistrations: 0,
    totalStudents: 0,
    approvedStudents: 0,
    activeCoaches: 0,
    activeHeadteachers: 0,
    weeklyRegistrations: 0
  });
  const [schoolStats, setSchoolStats] = useState<SchoolStats[]>([]);
  const [allRegistrations, setAllRegistrations] = useState<Registration[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [appSchools, setAppSchools] = useState<AppSchool[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [schoolLevels, setSchoolLevels] = useState<SchoolLevel[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadAllData();
    }
  }, [user]);

  useEffect(() => {
    setActiveTab(propActiveTab);
  }, [propActiveTab]);

  const loadAllData = async () => {
    try {
      setLoadingData(true);
      
      // Load all data
      const [registrationsData, usersData, schoolsData, appSchoolsData, regionsData, schoolLevelsData] = await Promise.all([
        dataService.getAllRegistrations(),
        dataService.getAllUsers(),
        dataService.getAllSchools(),
        dataService.getAppSchools(),
        dataService.getRegions(),
        dataService.getSchoolLevels()
      ]);

      setAllRegistrations(registrationsData);
      setAllUsers(usersData);
      setSchools(schoolsData);
      setAppSchools(appSchoolsData);
      setRegions(regionsData);
      setSchoolLevels(schoolLevelsData);

      // Calculate admin stats
      const adminStats: AdminStats = {
        totalSchools: schoolsData.length,
        totalUsers: usersData.length,
        totalRegistrations: registrationsData.length,
        pendingRegistrations: registrationsData.filter((r: any) => r.status === 'pending').length,
        approvedRegistrations: registrationsData.filter((r: any) => r.status === 'approved').length,
        rejectedRegistrations: registrationsData.filter((r: any) => r.status === 'rejected').length,
        // Calculate unique students by first name, last name, gender and date of birth
        totalStudents: new Set(
          registrationsData.map((r: any) => 
            `${(r.first_name || r.firstName || '').toLowerCase()}-${(r.last_name || r.lastName || '').toLowerCase()}-${r.gender || ''}-${r.date_of_birth || r.dateOfBirth || ''}`
          )
        ).size,
        // Calculate students with at least one approved registration
        approvedStudents: new Set(
          registrationsData
            .filter((r: any) => r.status === 'approved')
            .map((r: any) => 
              `${(r.first_name || r.firstName || '').toLowerCase()}-${(r.last_name || r.lastName || '').toLowerCase()}-${r.gender || ''}-${r.date_of_birth || r.dateOfBirth || ''}`
            )
        ).size,
        activeCoaches: usersData.filter((u: any) => u.role === 'coach').length,
        activeHeadteachers: usersData.filter((u: any) => u.role === 'headmaster').length,
        // Calculate average weekly registrations (last 4 weeks)
        weeklyRegistrations: calculateWeeklyRegistrations(registrationsData)
      };
      
      setStats(adminStats);

      // Calculate school-wise stats
      const schoolStatsData = schoolsData.map((school: any) => {
        const schoolRegistrations = registrationsData.filter((r: any) => (r.school_id || r.schoolId) === school.id);
        const schoolUsers = usersData.filter((u: any) => (u.school_id || u.schoolId) === school.id);
        const schoolStudents = new Set(schoolRegistrations.map((r: any) => `${r.first_name || r.firstName}-${r.last_name || r.lastName}-${r.date_of_birth || r.dateOfBirth}`)).size;

        return {
          school,
          registrations: schoolRegistrations.length,
          coaches: schoolUsers.filter((u: any) => u.role === 'coach').length,
          students: schoolStudents
        };
      });
      setSchoolStats(schoolStatsData);

    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        color: '#ef4444',
        fontSize: '1.2rem'
      }}>
        Access Denied - Admin privileges required
      </div>
    );
  }

  if (isLoading || loadingData) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A0E17 0%, #111827 50%, #0A0E17 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2rem'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '3px solid rgba(212, 175, 55, 0.2)',
          borderTop: '3px solid #FFD700',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{
          textAlign: 'center'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: '2px',
            background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            LOADING DASHBOARD
          </h2>
          <p style={{
            margin: '0.5rem 0 0 0',
            color: '#9CA3AF',
            fontSize: '0.9rem'
          }}>
            Please wait...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <DashboardHeader />
      <div className="dashboard-container">
      <div className="dashboard-wrapper">
        {/* Header */}
        <div className="dashboard-header-card">
          <div className="dashboard-header-content">
            <div>
              <h1 className="dashboard-page-title">
                Admin Dashboard
              </h1>
              <p className="dashboard-page-subtitle">
                System administration and oversight
              </p>
            </div>
            <div className="dashboard-actions">
              <button
                onClick={handleReports}
                className="dashboard-btn dashboard-btn-info"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 17H7A5 5 0 0 1 7 7h2m0 10a5 5 0 0 0 5-5 5 5 0 0 0-5-5m0 10V7m8 10V7m0 10a5 5 0 0 1-5-5 5 5 0 0 1 5-5"/>
                  <path d="M16 7h2a5 5 0 0 1 0 10h-2"/>
                </svg>
                Registration Report
              </button>
              <button
                onClick={toggleRegistrationStatus}
                className={`dashboard-btn ${isRegistrationOpen ? 'dashboard-btn-danger' : 'dashboard-btn-success'}`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {isRegistrationOpen ? (
                    <path d="M12 2L2 7v10c0 5.55 3.84 10 9 9 5.16 1 9-3.45 9-9V7l-10-5z"/>
                  ) : (
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  )}
                </svg>
                {isRegistrationOpen ? 'Close Registration' : 'Open Registration'}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="content-card">
          {activeTab === 'dashboard' && (
            <AdminStatsDashboard 
              stats={stats} 
              schoolStats={schoolStats}
              registrations={allRegistrations}
            />
          )}
          
          {activeTab === 'users' && (
            <AdminUsersManagement 
              users={allUsers}
              schools={schools}
              appSchools={appSchools}
              onRefresh={loadAllData}
            />
          )}
          
          {activeTab === 'registrations' && (
            <AdminRegistrationsManagement 
              registrations={allRegistrations}
              schools={schools}
              appSchools={appSchools}
              onRefresh={loadAllData}
              currentUser={user}
            />
          )}

          {activeTab === 'schools' && (
            <AdminSchoolManagement 
              smsSchools={schools}
              appSchools={appSchools}
              regions={regions}
              schoolLevels={schoolLevels}
              currentUser={user}
              onRefresh={loadAllData}
            />
          )}

          {activeTab === 'account' && user && (
            <UserAccountManager 
              currentUser={user}
              onUserUpdate={(updatedUser) => {
                // Update user context if needed
              }}
              schools={schools}
            />
          )}
        </div>
      </div>
      </div>
    </>
  );
};

// Admin Stats Dashboard Component
interface AdminStatsDashboardProps {
  stats: AdminStats;
  schoolStats: SchoolStats[];
  registrations: Registration[];
}

const AdminStatsDashboard: React.FC<AdminStatsDashboardProps> = ({ stats, schoolStats, registrations }) => {
  const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate real registration trends from database (weekly unique students)
  const registrationTrends = React.useMemo(() => {
    return calculateRegistrationTrends(registrations);
  }, [registrations]);

  // Calculate approval statistics based on unique students
  const approvalStats = React.useMemo(() => {
    return calculateApprovalStats(registrations);
  }, [registrations]);

  // Calculate real event participation data from registrations
  const eventParticipation = React.useMemo(() => {
    const eventCounts: { [key: string]: { male: number; female: number } } = {};
    
    // Only count approved registrations
    const approvedRegistrations = registrations.filter(reg => reg.status === 'approved');
    
    approvedRegistrations.forEach(reg => {
      if (reg.sport && reg.gender) {
        if (!eventCounts[reg.sport]) {
          eventCounts[reg.sport] = { male: 0, female: 0 };
        }
        
        if (reg.gender === 'male') {
          eventCounts[reg.sport].male += 1;
        } else if (reg.gender === 'female') {
          eventCounts[reg.sport].female += 1;
        }
      }
    });
    
    // Convert to array format for the chart and sort by total participation (descending)
    return Object.keys(eventCounts)
      .map(event => ({
        event: event.charAt(0).toUpperCase() + event.slice(1).replace('_', ' '),
        male: eventCounts[event].male,
        female: eventCounts[event].female,
        total: eventCounts[event].male + eventCounts[event].female
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10); // Show top 10 events
  }, [registrations]);

  // Age group distribution data - calculated from actual registrations
  const ageGroupData = React.useMemo(() => {
    return calculateAgeGroupDistribution(registrations);
  }, [registrations]);



  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95) 0%, rgba(17, 24, 39, 0.95) 100%)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(255, 215, 0, 0.1)',
    overflow: 'hidden' as const
  };

  const miniStatStyle = {
    background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95) 0%, rgba(17, 24, 39, 0.95) 100%)',
    padding: 'clamp(1rem, 2.5vw, 1.5rem)',
    borderRadius: 'clamp(12px, 2vw, 16px)',
    border: '1px solid rgba(255, 215, 0, 0.1)',
    transition: 'all 0.3s ease',
  };

  return (
    <div style={{ 
      padding: 'clamp(1rem, 4vw, 2rem) clamp(0.75rem, 3vw, 1.5rem)',
      background: 'transparent',
    }}>
      {/* Top Row - Key Stats */}
      <div className="stats-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 'clamp(1rem, 2.5vw, 1.5rem)',
        marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
      }}>
        {/* Average Weekly Registrations */}
        <div className="stat-card" style={{ ...miniStatStyle }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', fontWeight: '600', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Weekly Registrations
            </div>
            <div className="stat-card-badge positive">
              +8.5%
            </div>
          </div>
          <div className="stat-card-value success" style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: '0.5rem' }}>
            {stats.weeklyRegistrations}
          </div>
          <div style={{ height: '40px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={registrationTrends.slice(-4)}>
                <Area 
                  type="monotone" 
                  dataKey="registrations" 
                  stroke="#10b981" 
                  fill="rgba(16, 185, 129, 0.2)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Total Users */}
        <div className="stat-card" style={{ ...miniStatStyle }}>
          <div style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', fontWeight: '600', color: '#9CA3AF', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Total Users
          </div>
          <div className="stat-card-value info" style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: '0.75rem' }}>
            {stats.totalUsers}
          </div>
          <div style={{ fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)', color: '#6B7280', marginBottom: '0.75rem' }}>
            {stats.activeCoaches} coaches • {stats.activeHeadteachers} headteachers
          </div>
          {/* Mini bar visualization */}
          <div style={{ display: 'flex', gap: '4px', height: '24px', alignItems: 'flex-end' }}>
            <div style={{ 
              flex: stats.activeCoaches, 
              height: '100%', 
              background: 'linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%)', 
              borderRadius: '3px 3px 0 0',
              transition: 'all 0.5s ease',
              boxShadow: '0 0 8px rgba(59, 130, 246, 0.4)'
            }} />
            <div style={{ 
              flex: stats.activeHeadteachers, 
              height: '70%', 
              background: 'linear-gradient(180deg, #FFD700 0%, #B8860B 100%)', 
              borderRadius: '3px 3px 0 0',
              transition: 'all 0.5s ease',
              boxShadow: '0 0 8px rgba(255, 215, 0, 0.4)'
            }} />
          </div>
        </div>

        {/* Student Participation */}
        <div className="stat-card" style={{ ...miniStatStyle }}>
          <div style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', fontWeight: '600', color: '#9CA3AF', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Student Participation
          </div>
          <div className="stat-card-value warning" style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: '0.75rem' }}>
            {stats.totalStudents}
          </div>
          <div style={{ fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)', color: '#6B7280', marginBottom: '0.75rem' }}>
            {stats.totalRegistrations} total registrations
          </div>
          {/* Animated pulse dots */}
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {[...Array(Math.min(6, Math.ceil(stats.totalStudents / 100) || 1))].map((_, i) => (
              <div 
                key={i}
                style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  background: '#f59e0b',
                  boxShadow: '0 0 8px rgba(245, 158, 11, 0.6)',
                  animation: `pulse 1.5s ease-in-out ${i * 0.15}s infinite`
                }} 
              />
            ))}
            <div style={{ 
              flex: 1, 
              height: '4px', 
              background: 'rgba(245, 158, 11, 0.2)', 
              borderRadius: '2px',
              overflow: 'hidden',
              marginLeft: '4px'
            }}>
              <div style={{ 
                width: `${Math.min(100, (stats.totalStudents / 1000) * 100)}%`, 
                height: '100%', 
                background: 'linear-gradient(90deg, #f59e0b, #FFD700)',
                borderRadius: '2px',
                boxShadow: '0 0 10px rgba(245, 158, 11, 0.5)',
                animation: 'shimmer 2s ease-in-out infinite'
              }} />
            </div>
          </div>
        </div>

        {/* Approval Rate */}
        <div className="stat-card" style={{ ...miniStatStyle }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', fontWeight: '600', color: '#9CA3AF', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Student Approval Rate
              </div>
              <div className="stat-card-value success" style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: '0.5rem' }}>
                {approvalStats.approvalRate}%
              </div>
              <div style={{ fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)', color: '#6B7280' }}>
                {approvalStats.pendingStudents} of {approvalStats.totalStudents} students pending
              </div>
            </div>
            {/* Circular progress ring */}
            <div style={{ position: 'relative', width: '50px', height: '50px' }}>
              <svg width="50" height="50" style={{ transform: 'rotate(-90deg)' }}>
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  stroke="rgba(16, 185, 129, 0.2)"
                  strokeWidth="4"
                />
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={`${(approvalStats.approvalRate / 100) * 125.6} 125.6`}
                  style={{ 
                    transition: 'stroke-dasharray 1s ease-out',
                    filter: 'drop-shadow(0 0 6px rgba(16, 185, 129, 0.5))'
                  }}
                />
              </svg>
              <div style={{ 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)',
                fontSize: '0.65rem',
                fontWeight: '700',
                color: '#10b981'
              }}>
                ✓
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Charts Row */}
      <div className="admin-charts-grid" style={{
        display: 'grid',
        gridTemplateColumns: windowWidth < 900 ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: 'clamp(1rem, 3vw, 2rem)',
        marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
      }}>
        {/* Registration Trends Chart */}
        <div className="chart-container" style={{ ...cardStyle }}>
          <div style={{ padding: 'clamp(1.5rem, 3vw, 2rem) clamp(1.5rem, 3vw, 2rem) clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 3vw, 2rem)' }}>
            <h3 className="chart-title" style={{ 
              fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)', 
              fontWeight: '600', 
              margin: '0 0 0.5rem 0' 
            }}>
              Registration Trends
            </h3>
            <p style={{ 
              fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', 
              color: '#9CA3AF', 
              margin: '0 0 1.5rem 0' 
            }}>
              Weekly unique student registrations from start of registration period
            </p>
          </div>
          <div className="admin-chart-container" style={{ height: 'clamp(250px, 40vh, 300px)', padding: '0 1rem 1rem 1rem' }}>
            {registrationTrends.length === 0 || registrationTrends.every(week => week.registrations === 0) ? (
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center', 
                color: '#6B7280', 
                fontSize: '1rem',
                fontStyle: 'italic'
              }}>
                No registration activity found
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={registrationTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 215, 0, 0.1)" />
                <XAxis 
                  dataKey="week" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: windowWidth < 768 ? 10 : 12, fill: '#9CA3AF' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: windowWidth < 768 ? 10 : 12, fill: '#9CA3AF' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#1a1f2e',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    color: '#ffffff'
                  }}
                  labelStyle={{ color: '#FFD700' }}
                  itemStyle={{ color: '#ffffff' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="registrations" 
                  stackId="1"
                  stroke="#FFD700" 
                  fill="rgba(255, 215, 0, 0.15)"
                  strokeWidth={3}
                />
                <Area 
                  type="monotone" 
                  dataKey="approvals" 
                  stackId="2"
                  stroke="#10b981" 
                  fill="rgba(16, 185, 129, 0.15)"
                  strokeWidth={2}
                />
              </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Age Group Distribution */}
        <div className="chart-container" style={{ ...cardStyle }}>
          <div style={{ padding: 'clamp(1.5rem, 3vw, 2rem) clamp(1.5rem, 3vw, 2rem) clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 3vw, 2rem)' }}>
            <h3 className="chart-title" style={{ 
              fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)', 
              fontWeight: '600', 
              margin: '0 0 0.5rem 0' 
            }}>
              Age Group Distribution
            </h3>
            <p style={{ 
              fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', 
              color: '#9CA3AF', 
              margin: '0 0 1.5rem 0' 
            }}>
              Unique students by age groups
            </p>
          </div>
          <div className="admin-chart-container admin-pie-chart" style={{ 
            height: 'clamp(300px, 45vh, 400px)', 
            padding: '0 1rem 1rem 1rem', 
            display: 'flex', 
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            alignItems: 'center', 
            justifyContent: 'center',
            gap: windowWidth < 768 ? '1rem' : '0'
          }}>
            {ageGroupData.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                color: '#6B7280', 
                fontSize: '1rem',
                fontStyle: 'italic',
                padding: '2rem'
              }}>
                No students registered yet
              </div>
            ) : (
              <>
                <div style={{ 
                  width: windowWidth < 768 ? '100%' : '70%',
                  height: windowWidth < 768 ? '250px' : '100%'
                }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                  <Pie
                    data={ageGroupData}
                    cx="50%"
                    cy="50%"
                    innerRadius={windowWidth < 768 ? 40 : windowWidth < 1024 ? 50 : 60}
                    outerRadius={windowWidth < 768 ? 80 : windowWidth < 1024 ? 100 : 120}
                    paddingAngle={5}
                    dataKey="value"
                    animationBegin={0}
                    animationDuration={1200}
                    animationEasing="ease-out"
                    stroke="rgba(255, 215, 0, 0.2)"
                    strokeWidth={2}
                  >
                    {ageGroupData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.color}
                        style={{ filter: 'drop-shadow(0 0 6px rgba(255, 215, 0, 0.3))' }}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#1a1f2e',
                      border: '1px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                      color: '#ffffff',
                      padding: '12px 16px'
                    }}
                    labelStyle={{ color: '#FFD700', fontWeight: '600' }}
                    itemStyle={{ color: '#ffffff' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="admin-pie-legend" style={{ 
              marginLeft: windowWidth < 768 ? '0' : '1rem',
              marginTop: windowWidth < 768 ? '0' : '0',
              display: 'flex',
              flexDirection: windowWidth < 768 ? 'row' : 'column',
              flexWrap: windowWidth < 768 ? 'wrap' : 'nowrap',
              justifyContent: windowWidth < 768 ? 'center' : 'flex-start',
              gap: windowWidth < 768 ? '1rem' : '0'
            }}>
              {ageGroupData.map((entry, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: windowWidth < 768 ? '0.5rem' : '0.75rem',
                  minWidth: windowWidth < 768 ? 'auto' : 'unset',
                  whiteSpace: 'nowrap'
                }}>
                  <div style={{ 
                    width: windowWidth < 768 ? '10px' : '12px', 
                    height: windowWidth < 768 ? '10px' : '12px', 
                    borderRadius: '50%', 
                    backgroundColor: entry.color, 
                    marginRight: '0.5rem',
                    flexShrink: 0
                  }} />
                  <span style={{ 
                    fontSize: windowWidth < 768 ? '0.8rem' : '0.9rem', 
                    color: '#9CA3AF', 
                    fontWeight: '500' 
                  }}>
                    {entry.name}
                  </span>
                  <span style={{ 
                    fontSize: windowWidth < 768 ? '0.8rem' : '0.9rem', 
                    color: '#ffffff', 
                    fontWeight: '600', 
                    marginLeft: '0.5rem' 
                  }}>
                    {Math.round(entry.value)}
                  </span>
                </div>
              ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Event Participation Chart - Full Width */}
      <div className="chart-container" style={{ ...cardStyle, marginBottom: '2rem' }}>
        <div style={{ padding: 'clamp(1.5rem, 3vw, 2rem) clamp(1.5rem, 3vw, 2rem) clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 3vw, 2rem)' }}>
          <h3 className="chart-title" style={{ 
            fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)', 
            fontWeight: '600', 
            margin: '0 0 0.5rem 0' 
          }}>
            Event Participation
          </h3>
          <p style={{ 
            fontSize: 'clamp(0.8rem, 2vw, 0.9rem)', 
            color: '#9CA3AF', 
            margin: '0 0 1.5rem 0' 
          }}>
            Male vs Female participation
          </p>
        </div>
        <div className="admin-chart-container" style={{ height: 'clamp(300px, 50vh, 400px)', padding: '0 clamp(1rem, 3vw, 2rem) clamp(1.5rem, 3vw, 2rem) clamp(1rem, 3vw, 2rem)' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={eventParticipation} barCategoryGap="15%">
              <defs>
                <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FFD700" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#B8860B" stopOpacity={0.8}/>
                </linearGradient>
                <linearGradient id="crimsonGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C41E3A" stopOpacity={1}/>
                  <stop offset="100%" stopColor="#8B0000" stopOpacity={0.8}/>
                </linearGradient>
                <filter id="barGlow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 215, 0, 0.08)" vertical={false} />
              <XAxis 
                dataKey="event" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: windowWidth < 900 ? 10 : 12, fill: '#9CA3AF' }}
                angle={windowWidth < 600 ? -45 : 0}
                textAnchor={windowWidth < 600 ? "end" : "middle"}
                height={windowWidth < 600 ? 80 : 60}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6B7280' }}
              />
              <Tooltip 
                cursor={{ fill: 'rgba(255, 215, 0, 0.08)' }}
                contentStyle={{
                  backgroundColor: '#1a1f2e',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                  color: '#ffffff',
                  padding: '12px 16px'
                }}
                labelStyle={{ color: '#FFD700', fontWeight: '600', marginBottom: '8px' }}
                itemStyle={{ color: '#ffffff' }}
              />
              <Legend 
                wrapperStyle={{ color: '#9CA3AF', paddingTop: '1rem' }}
                iconType="circle"
              />
              <Bar 
                dataKey="male" 
                fill="url(#goldGradient)" 
                radius={[6, 6, 0, 0]} 
                name="Male"
                animationBegin={0}
                animationDuration={1200}
                animationEasing="ease-out"
              />
              <Bar 
                dataKey="female" 
                fill="url(#crimsonGradient)" 
                radius={[6, 6, 0, 0]} 
                name="Female"
                animationBegin={200}
                animationDuration={1200}
                animationEasing="ease-out"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  );
};

// Admin Users Management Component
interface AdminUsersManagementProps {
  users: User[];
  schools: School[];
  appSchools: AppSchool[];
  onRefresh: () => void;
}

const AdminUsersManagement: React.FC<AdminUsersManagementProps> = ({ users, schools, appSchools, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [showEditUserForm, setShowEditUserForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [schoolDropdownPosition, setSchoolDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const addSchoolInputRef = React.useRef<HTMLInputElement>(null);
  const editSchoolInputRef = React.useRef<HTMLInputElement>(null);
  const [schoolFilterSearch, setSchoolFilterSearch] = useState('');
  const [showSchoolFilterDropdown, setShowSchoolFilterDropdown] = useState(false);
  const [selectedSchoolForFilter, setSelectedSchoolForFilter] = useState<School | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  
  // Combine schools from both tables
  const allSchools = React.useMemo(() => {
    const sms = schools.map(s => ({ id: s.id, name: s.name }));
    const app = appSchools.map(s => ({ id: s.id, name: s.name }));
    return [...sms, ...app].sort((a, b) => a.name.localeCompare(b.name));
  }, [schools, appSchools]);
  
  // Mobile responsive states
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [activeUserDropdown, setActiveUserDropdown] = useState<string | null>(null);

  // Window resize effect
  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobile dropdown functions
  const toggleUserDropdown = (userId: string) => {
    setActiveUserDropdown(activeUserDropdown === userId ? null : userId);
  };

  const handleMobileEditUser = (user: User, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveUserDropdown(null);
    handleEditUser(user);
  };

  const handleMobileDeleteUser = (user: User, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveUserDropdown(null);
    handleDeleteUser(user);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.mobile-dropdown')) {
        setActiveUserDropdown(null);
      }
    };

    if (activeUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeUserDropdown]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'coach',
    schoolId: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // You can make this configurable if needed

  const filteredUsers = users.filter(user => {
    const userAny = user as any;
    const firstName = (user.firstName || userAny.first_name || '');
    const lastName = (user.lastName || userAny.last_name || '');
    const email = (user.email || '');
    
    const matchesSearch = 
      firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesSchool = !selectedSchoolForFilter || String(user.schoolId || userAny.school_id || '') === String(selectedSchoolForFilter.id);

    return matchesSearch && matchesRole && matchesSchool;
  });

  // Pagination calculations
  const totalUsers = filteredUsers.length;
  const totalPages = Math.ceil(totalUsers / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Check if mobile view
  const isMobile = windowWidth <= 768;

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, selectedSchoolForFilter]);

  // Close school filter dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-school-filter-dropdown]')) {
        setShowSchoolFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Clear school selection when role is changed to admin
      if (field === 'role' && value === 'admin') {
        newData.schoolId = '';
        setSchoolSearch('');
        setShowSchoolDropdown(false);
      }
      
      return newData;
    });
  };

  // Update dropdown position when it's shown — measure whichever form's input is open
  React.useEffect(() => {
    const inputEl = showEditUserForm ? editSchoolInputRef.current : addSchoolInputRef.current;
    if (showSchoolDropdown && inputEl) {
      const rect = inputEl.getBoundingClientRect();
      setSchoolDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, [showSchoolDropdown, schoolSearch, showAddUserForm, showEditUserForm]);

  const filteredSchools = allSchools.filter(school =>
    school.name.toLowerCase().includes(schoolSearch.toLowerCase())
  );

  const filteredSchoolsForFilter = schools.filter(school =>
    school.name.toLowerCase().includes(schoolFilterSearch.toLowerCase())
  );

  const handleSchoolSelect = (school: { id: string; name: string }) => {
    setFormData(prev => ({ ...prev, schoolId: school.id }));
    setSchoolSearch(school.name);
    setShowSchoolDropdown(false);
  };

  const handleSchoolFilterSelect = (school: School) => {
    setSelectedSchoolForFilter(school);
    setSchoolFilterSearch(school.name);
    setShowSchoolFilterDropdown(false);
  };

  const handleClearSchoolFilter = () => {
    setSelectedSchoolForFilter(null);
    setSchoolFilterSearch('');
    setShowSchoolFilterDropdown(false);
  };

  const handleSchoolSearch = (value: string) => {
    setSchoolSearch(value);
    setShowSchoolDropdown(true);
    // Clear the selected school if user is typing
    if (formData.schoolId && !schools.find(s => s.id === formData.schoolId)?.name.toLowerCase().includes(value.toLowerCase())) {
      setFormData(prev => ({ ...prev, schoolId: '' }));
    }
  };

  // Toast functionality
  const showToastNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  const handleAddUser = async () => {
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      showToastNotification(
        '⚠️ Missing Required Fields\n\nPlease fill in all required fields: Email, Password, First Name, and Last Name.',
        'error'
      );
      return;
    }

    // School is only required for non-admin roles
    if (formData.role !== 'admin' && !formData.schoolId) {
      showToastNotification(
        '🏫 School Required\n\nPlease select a school for this user. Only admin users can be created without a school assignment.',
        'error'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const userData: any = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role as 'coach' | 'headteacher' | 'admin',
        isActive: true
      };
      
      // Only add schoolId if it exists (admin users may not have a school)
      if (formData.schoolId) {
        userData.schoolId = formData.schoolId;
      }
      
      await dataService.createUser(userData);
      
      // Reset form and close popup
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'coach',
        schoolId: ''
      });
      setSchoolSearch('');
      setShowSchoolDropdown(false);
      setShowAddUserForm(false);
      onRefresh(); // Refresh the users list
      showToastNotification(
        `User Account Created\n\n${formData.firstName} ${formData.lastName} has been successfully added to the system with ${formData.role} privileges.`,
        'success'
      );
    } catch (error) {
      console.error('Error creating user:', error);
      showToastNotification(
        '❌ Creation Failed\n\nUnable to create the user account. Please check the email address and try again.',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = (user: User) => {
    const userAny = user as any;
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '', // Don't pre-fill password for security
      firstName: user.firstName || userAny.first_name || '',
      lastName: user.lastName || userAny.last_name || '',
      role: user.role,
      schoolId: String(user.schoolId || userAny.school_id || '')
    });
    const selectedSchool = schools.find(s => String(s.id) === String(user.schoolId || userAny.school_id));
    setSchoolSearch(selectedSchool?.name || '');
    setShowEditUserForm(true);
  };

  const handleUpdateUser = async () => {
    if (!formData.email || !formData.firstName || !formData.lastName || !editingUser) {
      showToastNotification(
        '⚠️ Missing Required Fields\n\nPlease fill in all required fields: Email, First Name, and Last Name.',
        'error'
      );
      return;
    }

    // School is only required for non-admin roles
    if (formData.role !== 'admin' && !formData.schoolId) {
      showToastNotification(
        '🏫 School Required\n\nPlease select a school for this user. Only admin users can be updated without a school assignment.',
        'error'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const updateData: any = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role as 'coach' | 'headteacher' | 'admin'
      };

      // Only add schoolId if it exists (admin users may not have a school)
      if (formData.schoolId) {
        updateData.schoolId = formData.schoolId;
      }

      // Only update password if provided
      if (formData.password.trim()) {
        updateData.password = formData.password;
      }

      await dataService.updateUser(editingUser.id, updateData);
      
      // Reset form and close popup
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'coach',
        schoolId: ''
      });
      setSchoolSearch('');
      setShowSchoolDropdown(false);
      setShowEditUserForm(false);
      setEditingUser(null);
      onRefresh(); // Refresh the users list
      showToastNotification(
        `📝 User Account Updated\n\n${formData.firstName} ${formData.lastName}'s profile has been successfully updated with the new information.`,
        'success'
      );
    } catch (error) {
      console.error('Error updating user:', error);
      showToastNotification(
        '❌ Update Failed\n\nUnable to save the changes to the user account. Please try again.',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setIsSubmitting(true);
      await dataService.deleteUser(userToDelete.id);
      onRefresh(); // Refresh the users list
      setShowDeleteConfirmation(false);
      setUserToDelete(null);
      
      const userAny = userToDelete as any;
      const userName = `${userToDelete.firstName || userAny.first_name || ''} ${userToDelete.lastName || userAny.last_name || ''}`.trim();
      showToastNotification(
        `🗑️ User Account Removed\n\n${userName} has been successfully removed from the system. Their access has been revoked and all associated data has been archived.`,
        'success'
      );
    } catch (error) {
      console.error('Error deleting user:', error);
      showToastNotification(
        '❌ Deletion Failed\n\nUnable to remove the user account. Please check your connection and try again.',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirmation(false);
    setUserToDelete(null);
  };

  return (
    <div style={{ padding: '2rem 1.5rem' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h2 style={{
          fontSize: '1.8rem',
          fontWeight: '600',
          color: '#FFD700',
          margin: 0,
          letterSpacing: '-0.025em'
        }}>
          User Management
        </h2>
        <button 
          onClick={() => setShowAddUserForm(true)}
          style={{
            background: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
            color: '#0A0E17',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '12px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Add New User
          </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: '1',
            minWidth: '250px',
            padding: '0.75rem 1rem',
            border: '2px solid rgba(255, 215, 0, 0.2)',
            borderRadius: '12px',
            fontSize: '0.95rem',
            transition: 'border-color 0.2s ease',
            background: '#111827',
            color: '#ffffff'
          }}
        />
        
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            border: '2px solid rgba(255, 215, 0, 0.2)',
            borderRadius: '12px',
            fontSize: '0.95rem',
            background: '#111827',
            color: '#ffffff',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Roles</option>
          <option value="admin">Admin</option>
          <option value="headteacher">Headteacher</option>
          <option value="coach">Coach</option>
        </select>

        <div style={{ position: 'relative', minWidth: '200px' }} data-school-filter-dropdown>
          <input
            type="text"
            placeholder={selectedSchoolForFilter ? selectedSchoolForFilter.name : "Search schools..."}
            value={schoolFilterSearch}
            onChange={(e) => {
              setSchoolFilterSearch(e.target.value);
              setShowSchoolFilterDropdown(true);
            }}
            onFocus={() => setShowSchoolFilterDropdown(true)}
            style={{
              width: '100%',
              padding: '0.75rem 2.5rem 0.75rem 1rem',
              border: '2px solid rgba(255, 215, 0, 0.2)',
              borderRadius: '12px',
              fontSize: '0.95rem',
              background: '#111827',
              color: '#ffffff',
              outline: 'none'
            }}
          />
          
          {selectedSchoolForFilter && (
            <button
              onClick={handleClearSchoolFilter}
              style={{
                position: 'absolute',
                right: '0.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: '#9CA3AF',
                cursor: 'pointer',
                fontSize: '1.2rem',
                padding: '0.25rem'
              }}
            >
              ×
            </button>
          )}

          {showSchoolFilterDropdown && (schoolFilterSearch || !selectedSchoolForFilter) && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: '#1a1f2e',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              borderRadius: '8px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.4)',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {!selectedSchoolForFilter && (
                <button
                  onClick={handleClearSchoolFilter}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    color: '#ffffff',
                    borderBottom: '1px solid rgba(255, 215, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(255, 215, 0, 0.1)'}
                  onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                >
                  All Schools
                </button>
              )}
              {filteredSchoolsForFilter.map(school => (
                <button
                  key={school.id}
                  onClick={() => handleSchoolFilterSelect(school)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    color: '#ffffff'
                  }}
                  onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(255, 215, 0, 0.1)'}
                  onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                >
                  {school.name}
                </button>
              ))}
              {filteredSchoolsForFilter.length === 0 && schoolFilterSearch && (
                <div style={{
                  padding: '0.75rem 1rem',
                  color: '#6B7280',
                  fontSize: '0.9rem'
                }}>
                  No schools found
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Users Table */}
      {isMobile ? (
        // Mobile Card View
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {paginatedUsers.map((user, index) => {
            const userAny = user as any;
            const firstName = user.firstName || userAny.first_name || '';
            const lastName = user.lastName || userAny.last_name || '';
            const schoolId = user.schoolId || userAny.school_id || '';
            
            // Role color mapping - Command Center theme
            const getRoleColors = (role: string) => {
              switch (role) {
                case 'admin':
                  return { bg: 'rgba(212, 175, 55, 0.15)', color: '#FFD700', border: 'rgba(212, 175, 55, 0.3)' }; // Gold
                case 'headteacher':
                case 'headmaster':
                  return { bg: 'rgba(196, 30, 58, 0.15)', color: '#C41E3A', border: 'rgba(196, 30, 58, 0.3)' }; // Crimson
                case 'coach':
                  return { bg: 'rgba(46, 139, 87, 0.15)', color: '#2E8B57', border: 'rgba(46, 139, 87, 0.3)' }; // Emerald
                default:
                  return { bg: 'rgba(107, 114, 128, 0.15)', color: '#9CA3AF', border: 'rgba(107, 114, 128, 0.3)' }; // Gray
              }
            };
            
            const roleColors = getRoleColors(user.role);

            return (
              <div 
                key={user.id}
                style={{
                  backgroundColor: '#111827',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 215, 0, 0.15)',
                  overflow: 'hidden'
                }}
              >
                {/* Mobile Header with name, role and 3-dot menu */}
                <div style={{
                  padding: '1rem',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: '600',
                      color: '#ffffff',
                      fontSize: '1rem',
                      marginBottom: '0.5rem'
                    }}>
                      {firstName} {lastName}
                    </div>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.375rem 0.75rem',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      background: roleColors.bg,
                      color: roleColors.color,
                      border: `1px solid ${roleColors.border}`
                    }}>
                      {user.role}
                    </span>
                  </div>
                  
                  {/* Three-dot menu */}
                  <div className="mobile-dropdown" style={{ position: 'relative' }}>
                    <button
                      onClick={() => toggleUserDropdown(user.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="12" cy="12" r="2" fill="#64748b"/>
                        <circle cx="12" cy="5" r="2" fill="#64748b"/>
                        <circle cx="12" cy="19" r="2" fill="#64748b"/>
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {activeUserDropdown === user.id && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        backgroundColor: '#1a1f2e',
                        borderRadius: '8px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 215, 0, 0.2)',
                        zIndex: 1000,
                        minWidth: '150px',
                        overflow: 'hidden'
                      }}>
                        <button
                          onClick={(e) => handleMobileEditUser(user, e)}
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            color: '#ffffff',
                            transition: 'background-color 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 215, 0, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="m18.5 2.5 a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Edit User
                        </button>
                        <button
                          onClick={(e) => handleMobileDeleteUser(user, e)}
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            color: '#C41E3A',
                            transition: 'background-color 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(196, 30, 58, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="3,6 5,6 21,6" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Delete User
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mobile Details */}
                <div style={{ padding: '1rem' }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem'
                  }}>
                    <div>
                      <div style={{
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#9CA3AF',
                        marginBottom: '0.25rem'
                      }}>
                        Email
                      </div>
                      <div style={{
                        color: '#ffffff',
                        fontSize: '0.9rem'
                      }}>
                        {user.email || 'N/A'}
                      </div>
                    </div>
                    
                    {schools.find(s => String(s.id) === String(schoolId))?.name && (
                      <div>
                        <div style={{
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          color: '#9CA3AF',
                          marginBottom: '0.25rem'
                        }}>
                          School
                        </div>
                        <div style={{
                          color: '#ffffff',
                          fontSize: '0.9rem'
                        }}>
                          {schools.find(s => String(s.id) === String(schoolId))?.name}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Desktop Table View
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 215, 0, 0.15)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)' }}>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'left', 
                  fontWeight: '600', 
                  color: '#FFD700',
                  fontSize: '0.95rem',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                }}>Name</th>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'left', 
                  fontWeight: '600', 
                  color: '#FFD700',
                  fontSize: '0.95rem',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                }}>Email</th>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'left', 
                  fontWeight: '600', 
                  color: '#FFD700',
                  fontSize: '0.95rem',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                }}>Role</th>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'left', 
                  fontWeight: '600', 
                  color: '#FFD700',
                  fontSize: '0.95rem',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                }}>School</th>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'left', 
                  fontWeight: '600', 
                  color: '#FFD700',
                  fontSize: '0.95rem',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                }}>Status</th>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'center', 
                  fontWeight: '600', 
                  color: '#FFD700',
                  fontSize: '0.95rem',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user, index) => {
                const userAny = user as any;
                const firstName = user.firstName || userAny.first_name || '';
                const lastName = user.lastName || userAny.last_name || '';
                const schoolId = user.schoolId || userAny.school_id || '';
                
                // Role color mapping - Command Center theme
                const getRoleColors = (role: string) => {
                  switch (role) {
                    case 'admin':
                      return { bg: 'rgba(212, 175, 55, 0.15)', color: '#FFD700', border: 'rgba(212, 175, 55, 0.3)' }; // Gold
                    case 'headteacher':
                    case 'headmaster':
                      return { bg: 'rgba(196, 30, 58, 0.15)', color: '#C41E3A', border: 'rgba(196, 30, 58, 0.3)' }; // Crimson
                    case 'coach':
                      return { bg: 'rgba(46, 139, 87, 0.15)', color: '#2E8B57', border: 'rgba(46, 139, 87, 0.3)' }; // Emerald
                    default:
                      return { bg: 'rgba(107, 114, 128, 0.15)', color: '#9CA3AF', border: 'rgba(107, 114, 128, 0.3)' }; // Gray
                  }
                };
                
                const roleColors = getRoleColors(user.role);
                
                return (
                <tr key={user.id} style={{ 
                  background: index % 2 === 0 ? '#111827' : '#0f1521',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.1)'
                }}>
                  <td style={{ 
                    padding: '1.25rem 1.5rem',
                    fontWeight: '600',
                    color: '#ffffff'
                  }}>
                    {firstName} {lastName}
                  </td>
                  <td style={{ 
                    padding: '1.25rem 1.5rem',
                    color: '#9CA3AF'
                  }}>
                    {user.email || 'N/A'}
                  </td>
                  <td style={{ padding: '1.25rem 1.5rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.375rem 0.75rem',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      background: roleColors.bg,
                      color: roleColors.color,
                      border: `1px solid ${roleColors.border}`
                    }}>
                      {user.role}
                    </span>
                  </td>
                  <td style={{ 
                    padding: '1.25rem 1.5rem',
                    color: '#9CA3AF'
                  }}>
                    {schools.find(s => String(s.id) === String(schoolId))?.name || 'N/A'}
                  </td>
                  <td style={{ padding: '1.25rem 1.5rem' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.375rem 0.75rem',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      background: 'rgba(16, 185, 129, 0.15)',
                      color: '#10b981'
                    }}>
                      Active
                    </span>
                  </td>
                  <td style={{ padding: '1.25rem 1.5rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button 
                        onClick={() => handleEditUser(user)}
                        style={{
                          padding: '0.5rem 1rem',
                          border: '1px solid rgba(255, 215, 0, 0.3)',
                          borderRadius: '8px',
                          background: 'transparent',
                          color: '#FFD700',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          (e.target as HTMLElement).style.background = 'rgba(255, 215, 0, 0.1)';
                          (e.target as HTMLElement).style.borderColor = '#FFD700';
                        }}
                        onMouseOut={(e) => {
                          (e.target as HTMLElement).style.background = 'transparent';
                          (e.target as HTMLElement).style.borderColor = 'rgba(255, 215, 0, 0.3)';
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user)}
                        style={{
                          padding: '0.5rem 1rem',
                          border: '1px solid rgba(196, 30, 58, 0.5)',
                          borderRadius: '8px',
                          background: 'rgba(196, 30, 58, 0.1)',
                          color: '#C41E3A',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          (e.target as HTMLElement).style.background = 'rgba(196, 30, 58, 0.2)';
                          (e.target as HTMLElement).style.borderColor = '#C41E3A';
                        }}
                        onMouseOut={(e) => {
                          (e.target as HTMLElement).style.background = 'rgba(196, 30, 58, 0.1)';
                          (e.target as HTMLElement).style.borderColor = 'rgba(196, 30, 58, 0.5)';
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: '2rem',
          padding: '1rem 0',
          borderTop: '1px solid rgba(255, 215, 0, 0.1)'
        }}>
          <div style={{ 
            fontSize: '0.9rem', 
            color: '#9CA3AF' 
          }}>
            Showing {startIndex + 1}-{Math.min(endIndex, totalUsers)} of {totalUsers} users
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem' 
          }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid rgba(255, 215, 0, 0.2)',
                borderRadius: '8px',
                backgroundColor: currentPage === 1 ? '#0f1521' : '#111827',
                color: currentPage === 1 ? '#6B7280' : '#ffffff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              Previous
            </button>
            
            <div style={{ 
              display: 'flex', 
              gap: '0.25rem' 
            }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '8px',
                    backgroundColor: page === currentPage ? '#FFD700' : '#111827',
                    color: page === currentPage ? '#0A0E17' : '#ffffff',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid rgba(255, 215, 0, 0.2)',
                borderRadius: '8px',
                backgroundColor: currentPage === totalPages ? '#0f1521' : '#111827',
                color: currentPage === totalPages ? '#6B7280' : '#ffffff',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add User Popup Form */}
      {showAddUserForm && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddUserForm(false);
              setSchoolSearch('');
              setShowSchoolDropdown(false);
              setFormData({
                email: '',
                password: '',
                firstName: '',
                lastName: '',
                role: 'coach',
                schoolId: ''
              });
            }
          }}
        >
          <div style={{
            backgroundColor: '#111827',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#FFD700',
                margin: 0
              }}>
                Add New User
              </h3>
              <button
                onClick={() => {
                  setShowAddUserForm(false);
                  setSchoolSearch('');
                  setShowSchoolDropdown(false);
                  setFormData({
                    email: '',
                    password: '',
                    firstName: '',
                    lastName: '',
                    role: 'coach',
                    schoolId: ''
                  });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#9CA3AF',
                  marginBottom: '0.5rem'
                }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    background: '#0A0E17',
                    color: '#ffffff'
                  }}
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#9CA3AF',
                  marginBottom: '0.5rem'
                }}>
                  Password *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleFormChange('password', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    background: '#0A0E17',
                    color: '#ffffff'
                  }}
                  placeholder="Enter password"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#9CA3AF',
                    marginBottom: '0.5rem'
                  }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleFormChange('firstName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      background: '#0A0E17',
                      color: '#ffffff'
                    }}
                    placeholder="First name"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#9CA3AF',
                    marginBottom: '0.5rem'
                  }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleFormChange('lastName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      background: '#0A0E17',
                      color: '#ffffff'
                    }}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#9CA3AF',
                  marginBottom: '0.5rem'
                }}>
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    backgroundColor: '#0A0E17',
                    color: '#ffffff'
                  }}
                >
                  <option value="coach">Coach</option>
                  <option value="headteacher">Headteacher</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {formData.role !== 'admin' && (
                <div style={{ position: 'relative' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#9CA3AF',
                    marginBottom: '0.5rem'
                  }}>
                    School *
                  </label>
                  <input
                    ref={addSchoolInputRef}
                    type="text"
                    value={schoolSearch}
                    onChange={(e) => handleSchoolSearch(e.target.value)}
                    onFocus={() => setShowSchoolDropdown(true)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      background: '#0A0E17',
                      color: '#ffffff'
                    }}
                    placeholder="Search and select a school..."
                  />
                  {showSchoolDropdown && filteredSchools.length > 0 && ReactDOM.createPortal(
                    <div style={{
                      position: 'fixed',
                      top: schoolDropdownPosition.top,
                      left: schoolDropdownPosition.left,
                      width: schoolDropdownPosition.width,
                      backgroundColor: '#1a1f2e',
                      border: '2px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: '8px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 10001,
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.5)'
                    }}>
                      {filteredSchools.map(school => (
                        <div
                          key={school.id}
                          onClick={() => handleSchoolSelect(school)}
                          style={{
                            padding: '0.75rem',
                            cursor: 'pointer',
                            borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
                            transition: 'background-color 0.2s ease',
                            fontSize: '1rem',
                            color: '#ffffff'
                          }}
                          onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(255, 215, 0, 0.1)'}
                          onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                        >
                          {school.name}
                        </div>
                      ))}
                    </div>,
                    document.body
                  )}
                  {showSchoolDropdown && filteredSchools.length === 0 && schoolSearch && ReactDOM.createPortal(
                    <div style={{
                      position: 'fixed',
                      top: schoolDropdownPosition.top,
                      left: schoolDropdownPosition.left,
                      width: schoolDropdownPosition.width,
                      backgroundColor: '#1a1f2e',
                      border: '2px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      color: '#6B7280',
                      fontSize: '0.9rem',
                      zIndex: 10001,
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.5)'
                    }}>
                      No schools found matching "{schoolSearch}"
                    </div>,
                    document.body
                  )}
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end',
                marginTop: '1rem'
              }}>
                <button
                  onClick={() => {
                    setShowAddUserForm(false);
                    setSchoolSearch('');
                    setShowSchoolDropdown(false);
                    setFormData({
                      email: '',
                      password: '',
                      firstName: '',
                      lastName: '',
                      role: 'coach',
                      schoolId: ''
                    });
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '8px',
                    backgroundColor: 'transparent',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddUser}
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '8px',
                    background: isSubmitting ? '#6B7280' : 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
                    color: '#0A0E17',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isSubmitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit User Popup Form */}
      {showEditUserForm && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowEditUserForm(false);
              setSchoolSearch('');
              setShowSchoolDropdown(false);
              setEditingUser(null);
              setFormData({
                email: '',
                password: '',
                firstName: '',
                lastName: '',
                role: 'coach',
                schoolId: ''
              });
            }
          }}
        >
          <div style={{
            backgroundColor: '#111827',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.4)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#FFD700',
                margin: 0
              }}>
                Edit User
              </h3>
              <button
                onClick={() => {
                  setShowEditUserForm(false);
                  setSchoolSearch('');
                  setShowSchoolDropdown(false);
                  setEditingUser(null);
                  setFormData({
                    email: '',
                    password: '',
                    firstName: '',
                    lastName: '',
                    role: 'coach',
                    schoolId: ''
                  });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#9CA3AF',
                  marginBottom: '0.5rem'
                }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    background: '#0A0E17',
                    color: '#ffffff'
                  }}
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#9CA3AF',
                  marginBottom: '0.5rem'
                }}>
                  Password (leave blank to keep current)
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleFormChange('password', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    background: '#0A0E17',
                    color: '#ffffff'
                  }}
                  placeholder="Enter new password (optional)"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#9CA3AF',
                    marginBottom: '0.5rem'
                  }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleFormChange('firstName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      background: '#0A0E17',
                      color: '#ffffff'
                    }}
                    placeholder="First name"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleFormChange('lastName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      background: '#0A0E17',
                      color: '#ffffff'
                    }}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#9CA3AF',
                  marginBottom: '0.5rem'
                }}>
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    backgroundColor: '#0A0E17',
                    color: '#ffffff'
                  }}
                >
                  <option value="coach">Coach</option>
                  <option value="headteacher">Headteacher</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {formData.role !== 'admin' && (
                <div style={{ position: 'relative' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#9CA3AF',
                    marginBottom: '0.5rem'
                  }}>
                    School *
                  </label>
                  <input
                    ref={editSchoolInputRef}
                    type="text"
                    value={schoolSearch}
                    onChange={(e) => handleSchoolSearch(e.target.value)}
                    onFocus={() => setShowSchoolDropdown(true)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      background: '#0A0E17',
                      color: '#ffffff'
                    }}
                    placeholder="Search and select a school..."
                  />
                  {showSchoolDropdown && filteredSchools.length > 0 && ReactDOM.createPortal(
                    <div style={{
                      position: 'fixed',
                      top: schoolDropdownPosition.top,
                      left: schoolDropdownPosition.left,
                      width: schoolDropdownPosition.width,
                      backgroundColor: '#1a1f2e',
                      border: '2px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: '8px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 10001,
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.5)'
                    }}>
                      {filteredSchools.map(school => (
                        <div
                          key={school.id}
                          onClick={() => handleSchoolSelect(school)}
                          style={{
                            padding: '0.75rem',
                            cursor: 'pointer',
                            borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
                            transition: 'background-color 0.2s ease',
                            fontSize: '1rem',
                            color: '#ffffff'
                          }}
                          onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = 'rgba(255, 215, 0, 0.1)'}
                          onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                        >
                          {school.name}
                        </div>
                      ))}
                    </div>,
                    document.body
                  )}
                  {showSchoolDropdown && filteredSchools.length === 0 && schoolSearch && ReactDOM.createPortal(
                    <div style={{
                      position: 'fixed',
                      top: schoolDropdownPosition.top,
                      left: schoolDropdownPosition.left,
                      width: schoolDropdownPosition.width,
                      backgroundColor: '#1a1f2e',
                      border: '2px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      color: '#6B7280',
                      fontSize: '0.9rem',
                      zIndex: 10001,
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.5)'
                    }}>
                      No schools found matching "{schoolSearch}"
                    </div>,
                    document.body
                  )}
              </div>
              )}

              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end',
                marginTop: '1rem'
              }}>
                <button
                  onClick={() => {
                    setShowEditUserForm(false);
                    setSchoolSearch('');
                    setShowSchoolDropdown(false);
                    setEditingUser(null);
                    setFormData({
                      email: '',
                      password: '',
                      firstName: '',
                      lastName: '',
                      role: 'coach',
                      schoolId: ''
                    });
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '8px',
                    backgroundColor: 'transparent',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateUser}
                  disabled={isSubmitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '8px',
                    background: isSubmitting ? '#6B7280' : 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
                    color: '#0A0E17',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isSubmitting ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && userToDelete && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              cancelDeleteUser();
            }
          }}
        >
          <div style={{
            backgroundColor: '#111827',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(196, 30, 58, 0.3)',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            {/* Warning Icon */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: 'rgba(196, 30, 58, 0.15)',
              margin: '0 auto 1.5rem auto'
            }}>
              <svg 
                width="40" 
                height="40" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#C41E3A" 
                strokeWidth="2"
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>

            {/* Title */}
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#C41E3A',
              marginBottom: '0.5rem',
              textAlign: 'center'
            }}>
              Delete User Account
            </h2>

            {/* Description */}
            <p style={{
              fontSize: '1rem',
              color: '#9CA3AF',
              marginBottom: '1.5rem',
              textAlign: 'center',
              lineHeight: '1.6'
            }}>
              Are you sure you want to delete <strong style={{ color: '#ffffff' }}>
                {((userToDelete as any).first_name || userToDelete.firstName || '')} {((userToDelete as any).last_name || userToDelete.lastName || '')}
              </strong>? This action cannot be undone and will permanently remove their access to the system.
            </p>

            {/* User Info Card */}
            <div style={{
              backgroundColor: '#0A0E17',
              border: '1px solid rgba(255, 215, 0, 0.15)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{ fontSize: '0.9rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>
                <strong style={{ color: '#FFD700' }}>Email:</strong> {(userToDelete as any).email}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#9CA3AF', marginBottom: '0.5rem' }}>
                <strong style={{ color: '#FFD700' }}>Role:</strong> {((userToDelete as any).role || '').toUpperCase()}
              </div>
              {(userToDelete as any).school_name && (
                <div style={{ fontSize: '0.9rem', color: '#9CA3AF' }}>
                  <strong style={{ color: '#FFD700' }}>School:</strong> {(userToDelete as any).school_name}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center'
            }}>
              <button
                onClick={cancelDeleteUser}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  border: '2px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '120px'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 215, 0, 0.1)';
                    e.currentTarget.style.borderColor = '#FFD700';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                  }
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteUser}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  borderRadius: '8px',
                  background: isSubmitting ? '#6B7280' : 'linear-gradient(135deg, #C41E3A 0%, #a01830 100%)',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '120px',
                  boxShadow: isSubmitting ? 'none' : '0 4px 6px -1px rgba(196, 30, 58, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 8px -1px rgba(196, 30, 58, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(196, 30, 58, 0.3)';
                  }
                }}
              >
                {isSubmitting ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Deleting...
                  </div>
                ) : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div 
          style={{
            position: 'fixed',
            top: '2rem',
            right: '2rem',
            zIndex: 1200,
            minWidth: '350px',
            maxWidth: '500px'
          }}
        >
          <div style={{
            backgroundColor: '#111827',
            border: `2px solid ${toastType === 'success' ? '#FFD700' : '#C41E3A'}`,
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.4)',
            animation: 'slideIn 0.3s ease-out',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <div style={{
                flexShrink: 0,
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: toastType === 'success' ? '#FFD700' : '#C41E3A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '0.125rem'
              }}>
                {toastType === 'success' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#0A0E17">
                    <path d="M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                    <path d="M24 20.188l-8.315-8.209 8.2-8.282-3.697-3.697-8.212 8.318-8.31-8.203-3.666 3.666 8.321 8.24-8.206 8.313 3.666 3.666 8.237-8.318 8.285 8.203z"/>
                  </svg>
                )}
              </div>
              
              <div style={{ flex: 1 }}>
                <pre style={{ 
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  fontSize: '0.95rem', 
                  lineHeight: '1.6',
                  color: toastType === 'success' ? '#FFD700' : '#C41E3A',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  fontWeight: '500'
                }}>
                  {toastMessage}
                </pre>
              </div>
              
              <button
                onClick={() => setShowToast(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  borderRadius: '4px',
                  color: '#9CA3AF',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  opacity: 0.6,
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Registrations Management Component
interface AdminRegistrationsManagementProps {
  registrations: Registration[];
  schools: School[];
  appSchools: AppSchool[];
  onRefresh: () => void;
  currentUser: any;
}

const AdminRegistrationsManagement: React.FC<AdminRegistrationsManagementProps> = ({ registrations, schools, appSchools, onRefresh, currentUser }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [schoolSearchTerm, setSchoolSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(3);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [isRemovingEvent, setIsRemovingEvent] = useState(false);
  
  // Name editing state for admin
  const [editedFirstName, setEditedFirstName] = useState('');
  const [editedLastName, setEditedLastName] = useState('');
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  
  // Admin Registration states
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({
    firstName: '',
    lastName: '',
    gender: 'male' as 'male' | 'female',
    dateOfBirth: '',
    schoolId: '',
    sports: [] as string[],
    ageGroup: '' // For 13-year-old manual selection
  });
  const [isSubmittingRegistration, setIsSubmittingRegistration] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const [registrationSchoolSearch, setRegistrationSchoolSearch] = useState('');
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const schoolInputRef = React.useRef<HTMLInputElement>(null);
  
  // Update dropdown position when showing
  React.useEffect(() => {
    if (showSchoolDropdown && schoolInputRef.current) {
      const rect = schoolInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, [showSchoolDropdown]);
  
  // Combine SMS schools and App schools for the dropdown
  const allSchoolsForRegistration = React.useMemo(() => {
    const smsSchoolsList = schools.map(s => ({ id: s.id, name: s.name, source: 'SMS' as const }));
    const appSchoolsList = appSchools.map(s => ({ id: s.id, name: s.name, source: 'App' as const }));
    return [...smsSchoolsList, ...appSchoolsList].sort((a, b) => a.name.localeCompare(b.name));
  }, [schools, appSchools]);
  
  // Filter schools based on search
  const filteredSchoolsForRegistration = React.useMemo(() => {
    if (!registrationSchoolSearch.trim()) return allSchoolsForRegistration.slice(0, 50); // Show first 50 if no search
    return allSchoolsForRegistration.filter(s => 
      s.name.toLowerCase().includes(registrationSchoolSearch.toLowerCase())
    ).slice(0, 50);
  }, [allSchoolsForRegistration, registrationSchoolSearch]);
  
  // Get selected school name
  const selectedSchoolName = React.useMemo(() => {
    const selected = allSchoolsForRegistration.find(s => s.id === registrationForm.schoolId);
    return selected?.name || '';
  }, [allSchoolsForRegistration, registrationForm.schoolId]);
  
  const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load events for registration form
  React.useEffect(() => {
    const loadEvents = async () => {
      try {
        const eventsData = await dataService.getEvents();
        setEvents(eventsData);
      } catch (error) {
        console.error('Error loading events:', error);
      }
    };
    
    if (showRegistrationForm) {
      loadEvents();
    }
  }, [showRegistrationForm]);

  // Helper functions for 13-year-old age group selection
  const getAgeAtEndOfYear = (dateOfBirth: string) => {
    if (!dateOfBirth) return 0;
    const dob = new Date(dateOfBirth);
    const currentYear = new Date().getFullYear();
    const endOfYear = new Date(currentYear, 11, 31); // December 31 of current year
    return endOfYear.getFullYear() - dob.getFullYear();
  };

  const getAgeGroup = (dateOfBirth: string) => {
    const age = getAgeAtEndOfYear(dateOfBirth);
    if (age <= 12) return 'Grade 7';
    if (age <= 14) return 'Under 15';
    if (age <= 16) return 'Under 17';
    if (age <= 19) return 'Under 20';
    return 'Open';
  };

  const is13YearsOld = (dob: string) => {
    return getAgeAtEndOfYear(dob) === 13;
  };

  const getEffectiveAgeGroup = (student: any) => {
    if (is13YearsOld(student.dateOfBirth) && student.ageGroup) {
      return student.ageGroup; // Use manual selection for 13-year-olds
    }
    return getAgeGroup(student.dateOfBirth); // Use automatic calculation for others
  };

  // Mobile dropdown states
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const toggleDropdown = (studentKey: string) => {
    setActiveDropdown(activeDropdown === studentKey ? null : studentKey);
  };

  const handleEditClick = (group: any) => {
    setSelectedStudent(group);
    setEditedFirstName(group.studentInfo.firstName || '');
    setEditedLastName(group.studentInfo.lastName || '');
    setShowEditModal(true);
    setActiveDropdown(null);
  };
  
  // Toast notification states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  
  // Add event states
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [isAddingEvent, setIsAddingEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState('');

  // Group registrations by student
  const groupedRegistrations = registrations.reduce((groups: any, registration: any) => {
    const firstName = registration.first_name || registration.firstName || '';
    const lastName = registration.last_name || registration.lastName || '';
    const studentKey = `${firstName}_${lastName}_${registration.date_of_birth || registration.dateOfBirth}`;
    
    if (!groups[studentKey]) {
      groups[studentKey] = {
        studentInfo: {
          firstName,
          lastName,
          dateOfBirth: registration.date_of_birth || registration.dateOfBirth,
          gender: registration.gender,
          schoolId: registration.school_id || registration.schoolId,
          schoolName: schools.find(s => s.id === (registration.school_id || registration.schoolId))?.name || 'Unknown'
        },
        registrations: []
      };
    }
    
    groups[studentKey].registrations.push({
      id: registration.id,
      sport: registration.sport || registration.eventName || 'Unknown Event',
      ageGroup: registration.age_group || registration.ageGroup || 'Unknown',
      status: registration.status,
      date: registration.created_at || registration.registrationDate || '',
      statusDate: registration.status_date
    });
    
    return groups;
  }, {});

  // Convert to array and filter
  const studentRegistrations = Object.values(groupedRegistrations).filter((group: any) => {
    const { studentInfo, registrations: studentRegs } = group;
    
    const matchesSearch = 
      studentInfo.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      studentInfo.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      studentRegs.some((reg: any) => reg.sport.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || 
      studentRegs.some((reg: any) => reg.status === statusFilter);
    
    const matchesSchool = schoolSearchTerm === '' || 
      studentInfo.schoolName.toLowerCase().includes(schoolSearchTerm.toLowerCase());

    return matchesSearch && matchesStatus && matchesSchool;
  });

  // Pagination logic
  const totalPages = Math.ceil(studentRegistrations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStudents = studentRegistrations.slice(startIndex, endIndex);

  // Check if mobile view
  const isMobile = windowWidth <= 768;

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, schoolSearchTerm]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      setActiveDropdown(null);
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeDropdown]);

  // Toast functionality
  const showToastNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  const handleEditStudent = (group: any) => {
    setSelectedStudent(group);
    setEditedFirstName(group.studentInfo.firstName || '');
    setEditedLastName(group.studentInfo.lastName || '');
    setShowEditModal(true);
  };

  const handleRemoveEvent = async (registrationId: string) => {
    if (!selectedStudent) return;
    
    try {
      setIsRemovingEvent(true);
      // Call the dataService to delete the registration
      await dataService.deleteRegistration(registrationId);
      
      // Update the selected student's registrations
      const updatedRegistrations = selectedStudent.registrations.filter(
        (reg: any) => reg.id !== registrationId
      );
      
      if (updatedRegistrations.length === 0) {
        // If no more events, close modal and refresh
        setShowEditModal(false);
        setSelectedStudent(null);
      } else {
        // Update the selected student with remaining events
        setSelectedStudent({
          ...selectedStudent,
          registrations: updatedRegistrations
        });
      }
      
      // Refresh the main data
      onRefresh();
      
      // Show success message
      showToastNotification('Event registration removed successfully!', 'success');
    } catch (error) {
      console.error('Error removing event:', error);
      showToastNotification('Failed to remove event. Please try again.', 'error');
    } finally {
      setIsRemovingEvent(false);
    }
  };

  const handleDeleteStudent = async () => {
    if (!selectedStudent) return;
    
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ALL event registrations for ${selectedStudent.studentInfo.firstName} ${selectedStudent.studentInfo.lastName}? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;
    
    try {
      setIsRemovingEvent(true);
      
      // Delete all registrations for this student
      for (const registration of selectedStudent.registrations) {
        await dataService.deleteRegistration(registration.id);
      }
      
      // Close modal and refresh data
      setShowEditModal(false);
      setSelectedStudent(null);
      onRefresh();
      
      // Show success message
      showToastNotification(`All event registrations for ${selectedStudent.studentInfo.firstName} ${selectedStudent.studentInfo.lastName} have been deleted successfully.`, 'success');
    } catch (error) {
      console.error('Error deleting student registrations:', error);
      showToastNotification('Failed to delete student registrations. Please try again.', 'error');
    } finally {
      setIsRemovingEvent(false);
    }
  };

  // Function to check if a school can register more students for a specific event
  const checkEventAvailability = (eventName: string, ageGroup: string, gender: string, schoolId: string) => {
    // Count current registrations for this event/age group/gender/school combination
    const currentRegistrations = registrations.filter(reg => 
      reg.sport === eventName &&
      reg.age_group === ageGroup &&
      reg.gender === gender &&
      reg.school_id === schoolId &&
      !reg.deleted_at // Don't count soft deleted registrations
    );
    
    return currentRegistrations.length < 3; // Max 3 students per event/gender/age/school
  };

  // Get available events for a student
  const getAvailableEvents = (student: any) => {
    const events = [
      '100m', '200m', '400m', '800m', '1000m', '1500m', '3000m',
      'shot_put', 'javelin', 'discus', 'high_jump', 'long_jump', 'triple_jump'
    ];
    
    const studentGender = student.studentInfo.gender;
    const studentAgeGroup = student.registrations[0]?.ageGroup || 'Under 17'; // Fallback
    const studentSchoolId = student.studentInfo.schoolId;
    
    // Get events the student is already registered for
    const registeredEvents = student.registrations.map((reg: any) => reg.sport);
    
    // Filter available events
    return events.filter(eventName => 
      !registeredEvents.includes(eventName) && // Not already registered
      checkEventAvailability(eventName, studentAgeGroup, studentGender, studentSchoolId)
    );
  };

  // Function to add an event to a student
  const handleAddEvent = async () => {
    if (!selectedStudent || !selectedEvent) return;
    
    try {
      setIsAddingEvent(true);
      
      // Create registration data
      const registrationData = {
        first_name: selectedStudent.studentInfo.firstName,
        last_name: selectedStudent.studentInfo.lastName,
        date_of_birth: selectedStudent.studentInfo.dateOfBirth,
        gender: selectedStudent.studentInfo.gender as 'male' | 'female',
        school_id: selectedStudent.studentInfo.schoolId,
        sport: selectedEvent,
        age_group: selectedStudent.registrations[0]?.ageGroup || 'Under 17',
        status: 'pending',
        created_by: currentUser?.id || 'admin'
      };
      
      // Add to database
      const newRegistration = await dataService.createRegistration(registrationData);
      
      // Update the selected student's registrations in local state
      const updatedRegistrations = [...selectedStudent.registrations, {
        id: newRegistration.id,
        sport: selectedEvent,
        ageGroup: registrationData.age_group,
        status: 'pending',
        date: new Date().toISOString(),
        statusDate: null
      }];
      
      setSelectedStudent({
        ...selectedStudent,
        registrations: updatedRegistrations
      });
      
      // Close add event modal and refresh data
      setShowAddEventModal(false);
      setSelectedEvent('');
      onRefresh();
      
      // Show success message
      showToastNotification(`${selectedEvent} event added successfully to ${selectedStudent.studentInfo.firstName} ${selectedStudent.studentInfo.lastName}!`, 'success');
    } catch (error) {
      console.error('Error adding event:', error);
      showToastNotification('Failed to add event. Please try again.', 'error');
    } finally {
      setIsAddingEvent(false);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedStudent(null);
    setShowAddEventModal(false);
    setSelectedEvent('');
    setEditedFirstName('');
    setEditedLastName('');
  };

  const handleSaveStudentName = async () => {
    if (!selectedStudent) return;

    try {
      setIsUpdatingName(true);

      // Validate input
      if (!editedFirstName.trim() || !editedLastName.trim()) {
        showToastNotification('First name and last name are required', 'error');
        return;
      }

      // Update the student name for all their registrations
      const updatePromises = selectedStudent.registrations.map((registration: any) => 
        dataService.updateStudentName(
          registration.id,
          editedFirstName.trim(),
          editedLastName.trim()
        )
      );

      await Promise.all(updatePromises);

      // Update the selected student state with new names
      setSelectedStudent({
        ...selectedStudent,
        studentInfo: {
          ...selectedStudent.studentInfo,
          firstName: editedFirstName.trim(),
          lastName: editedLastName.trim()
        }
      });

      // Refresh the registrations data
      onRefresh();

      // Show success message
      showToastNotification('Student name updated successfully', 'success');

    } catch (error) {
      console.error('Error updating student name:', error);
      showToastNotification('Failed to update student name. Please try again.', 'error');
    } finally {
      setIsUpdatingName(false);
    }
  };

  // Registration form handlers
  const handleRegistrationFormChange = (field: string, value: string) => {
    setRegistrationForm(prev => ({
      ...prev,
      [field]: value,
      // Clear sports and ageGroup when dateOfBirth changes
      ...(field === 'dateOfBirth' && { sports: [], ageGroup: '' })
    }));
  };

  const handleRegistrationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!registrationForm.firstName.trim()) {
      showToastNotification('First name is required', 'error');
      return;
    }
    if (!registrationForm.lastName.trim()) {
      showToastNotification('Last name is required', 'error');
      return;
    }
    if (!registrationForm.dateOfBirth) {
      showToastNotification('Date of birth is required', 'error');
      return;
    }
    if (!registrationForm.schoolId) {
      showToastNotification('School is required', 'error');
      return;
    }
    if (is13YearsOld(registrationForm.dateOfBirth) && !registrationForm.ageGroup) {
      showToastNotification('Please select age group for 13-year-old student (Grade 7 or Under 15)', 'error');
      return;
    }
    if (registrationForm.sports.length === 0) {
      showToastNotification('Please select at least one event', 'error');
      return;
    }

    try {
      setIsSubmittingRegistration(true);
      
      const ageGroup = getEffectiveAgeGroup(registrationForm);

      // Create registrations for each selected sport
      const registrationPromises = registrationForm.sports.map(sport =>
        dataService.createRegistration({
          first_name: registrationForm.firstName.trim(),
          last_name: registrationForm.lastName.trim(),
          gender: registrationForm.gender,
          date_of_birth: registrationForm.dateOfBirth,
          school_id: registrationForm.schoolId,
          sport: sport,
          age_group: ageGroup,
          status: 'approved', // Admin can directly approve
          created_by: currentUser.id
        })
      );

      await Promise.all(registrationPromises);

      // Reset form
      setRegistrationForm({
        firstName: '',
        lastName: '',
        gender: 'male',
        dateOfBirth: '',
        schoolId: '',
        sports: [],
        ageGroup: ''
      });
      
      setShowRegistrationForm(false);
      onRefresh();
      
      const eventNames = registrationForm.sports.map(sportId => 
        events.find(e => e.id === sportId)?.name || sportId
      ).join(', ');
      
      showToastNotification(
        `${registrationForm.firstName} ${registrationForm.lastName} has been successfully registered for ${eventNames}!`, 
        'success'
      );

    } catch (error) {
      console.error('Error creating registration:', error);
      showToastNotification('Failed to create registration. Please try again.', 'error');
    } finally {
      setIsSubmittingRegistration(false);
    }
  };

  return (
    <div style={{ padding: '2rem 1.5rem' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: '600',
            color: '#FFD700',
            margin: 0,
            letterSpacing: '-0.025em'
          }}>
            Registration Management
          </h2>
          <div style={{ 
            display: 'flex',
            gap: '1rem',
            fontSize: '0.9rem',
            color: '#9CA3AF',
            marginTop: '0.5rem'
          }}>
            <span style={{ 
              background: '#111827',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              Total: {Object.keys(groupedRegistrations).length} Students
            </span>
            <span style={{ 
              background: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
              color: '#0A0E17',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontWeight: '600'
            }}>
              Filtered: {studentRegistrations.length} Students
            </span>
          </div>
        </div>
        
        <button
          onClick={() => setShowRegistrationForm(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Register New Student
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search registrations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: '1',
            minWidth: '250px',
            padding: '0.75rem 1rem',
            border: '2px solid rgba(255, 215, 0, 0.2)',
            borderRadius: '12px',
            fontSize: '0.95rem',
            transition: 'border-color 0.2s ease',
            background: '#111827',
            color: '#ffffff'
          }}
        />
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            border: '2px solid rgba(255, 215, 0, 0.2)',
            borderRadius: '12px',
            fontSize: '0.95rem',
            background: '#111827',
            color: '#ffffff',
            cursor: 'pointer'
          }}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <input
          type="text"
          placeholder="Search schools..."
          value={schoolSearchTerm}
          onChange={(e) => setSchoolSearchTerm(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            border: '2px solid rgba(255, 215, 0, 0.2)',
            borderRadius: '12px',
            fontSize: '0.95rem',
            background: '#111827',
            color: '#ffffff',
            minWidth: '200px'
          }}
        />
      </div>

      {/* Registrations Table */}
      {isMobile ? (
        // Mobile Card View
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {paginatedStudents.map((group: any, index: number) => {
            const { studentInfo, registrations: studentRegs } = group;
            const studentKey = `${studentInfo.firstName}_${studentInfo.lastName}_${studentInfo.dateOfBirth}`;

            return (
              <div 
                key={studentKey}
                style={{
                  backgroundColor: '#111827',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 215, 0, 0.15)',
                  overflow: 'hidden'
                }}
              >
                {/* Mobile Header with 3-dot menu */}
                <div style={{
                  padding: '1rem',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: '600',
                      color: '#ffffff',
                      fontSize: '1rem',
                      marginBottom: '0.25rem'
                    }}>
                      {studentInfo.firstName} {studentInfo.lastName}
                    </div>
                    <div style={{
                      fontSize: '0.875rem',
                      color: '#9CA3AF'
                    }}>
                      {studentInfo.gender} • {studentInfo.dateOfBirth ? new Date(studentInfo.dateOfBirth).toLocaleDateString() : 'Unknown'}
                    </div>
                  </div>
                  
                  {/* Three-dot menu */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => toggleDropdown(studentKey)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 215, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle cx="12" cy="12" r="2" fill="#9CA3AF"/>
                        <circle cx="12" cy="5" r="2" fill="#9CA3AF"/>
                        <circle cx="12" cy="19" r="2" fill="#9CA3AF"/>
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {activeDropdown === studentKey && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        backgroundColor: '#1a1f2e',
                        borderRadius: '8px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 215, 0, 0.2)',
                        zIndex: 1000,
                        minWidth: '150px',
                        overflow: 'hidden'
                      }}>
                        <button
                          onClick={() => handleEditClick(group)}
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            color: '#ffffff',
                            transition: 'background-color 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 215, 0, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="m18.5 2.5 a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Edit Student
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Events & Status Section */}
                <div style={{ padding: '1rem' }}>
                  <div style={{
                    fontWeight: '600',
                    color: '#FFD700',
                    fontSize: '0.9rem',
                    marginBottom: '0.75rem'
                  }}>
                    Events & Status
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {studentRegs.map((reg: any, regIndex: number) => (
                      <div 
                        key={reg.id} 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.75rem',
                          backgroundColor: '#0A0E17',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 215, 0, 0.1)'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontWeight: '600',
                            color: '#374151',
                            fontSize: '0.9rem',
                            marginBottom: '0.25rem'
                          }}>
                            {reg.sport}
                          </div>
                          <div style={{
                            fontSize: '0.8rem',
                            color: '#64748b'
                          }}>
                            {reg.ageGroup}
                          </div>
                        </div>
                        <div style={{ marginLeft: '1rem' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.375rem 0.75rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: 
                              reg.status === 'approved' ? '#dcfce7' :
                              reg.status === 'pending' ? '#fef3c7' :
                              reg.status === 'rejected' ? '#fecaca' :
                              '#f3f4f6',
                            color: 
                              reg.status === 'approved' ? '#166534' :
                              reg.status === 'pending' ? '#92400e' :
                              reg.status === 'rejected' ? '#dc2626' :
                              '#374151'
                          }}>
                            {reg.status ? reg.status.toUpperCase() : 'UNKNOWN'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Desktop Table View
        <div style={{
          overflowX: 'auto',
          borderRadius: '16px',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 215, 0, 0.15)',
          background: '#111827'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)' }}>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'left', 
                  fontWeight: '700', 
                  color: '#FFD700',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                }}>Student</th>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'left', 
                  fontWeight: '700', 
                  color: '#FFD700',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)',
                  minWidth: '300px'
                }}>Events & Status</th>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'left', 
                  fontWeight: '700', 
                  color: '#FFD700',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                }}>School</th>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'left', 
                  fontWeight: '700', 
                  color: '#FFD700',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                }}>Registration Date</th>
                <th style={{ 
                  padding: '1.5rem 1.5rem', 
                  textAlign: 'center', 
                  fontWeight: '700', 
                  color: '#FFD700',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedStudents.map((group: any, index: number) => {
                const { studentInfo, registrations: studentRegs } = group;
                const earliestDate = studentRegs.reduce((earliest: string, reg: any) => {
                  const regDate = reg.date || '';
                  if (!earliest || (regDate && new Date(regDate) < new Date(earliest))) {
                    return regDate;
                  }
                  return earliest;
                }, '');

                return (
                  <tr key={`${studentInfo.firstName}_${studentInfo.lastName}_${studentInfo.dateOfBirth}`} style={{ 
                    background: index % 2 === 0 ? '#111827' : '#0f1521',
                    borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
                    transition: 'background 0.2s ease'
                  }}>
                    <td style={{ padding: '1.25rem 1.5rem' }}>
                      <div>
                        <div style={{
                          fontWeight: '600',
                          color: '#ffffff',
                          fontSize: '0.95rem',
                          marginBottom: '0.25rem'
                        }}>
                          {studentInfo.firstName} {studentInfo.lastName}
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: '#9CA3AF'
                        }}>
                          {studentInfo.gender} • Born: {studentInfo.dateOfBirth ? new Date(studentInfo.dateOfBirth).toLocaleDateString() : 'Unknown'}
                        </div>
                      </div>
                    </td>
                    <td style={{ 
                      padding: '1.25rem 1.5rem',
                      verticalAlign: 'top'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {studentRegs.map((reg: any, regIndex: number) => (
                          <div 
                            key={reg.id} 
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.75rem',
                              backgroundColor: '#0A0E17',
                              borderRadius: '8px',
                              border: '1px solid rgba(255, 215, 0, 0.15)'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontWeight: '600',
                                color: '#ffffff',
                                fontSize: '0.9rem',
                                marginBottom: '0.25rem'
                              }}>
                                {reg.sport}
                              </div>
                              <div style={{
                                fontSize: '0.8rem',
                                color: '#9CA3AF'
                              }}>
                                Age Group: {reg.ageGroup}
                              </div>
                            </div>
                            <div style={{ marginLeft: '1rem' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '0.375rem 0.75rem',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                backgroundColor: 
                                  reg.status === 'approved' ? 'rgba(34, 197, 94, 0.2)' :
                                  reg.status === 'pending' ? 'rgba(255, 215, 0, 0.2)' :
                                  reg.status === 'rejected' ? 'rgba(239, 68, 68, 0.2)' :
                                  'rgba(107, 114, 128, 0.2)',
                                color: 
                                  reg.status === 'approved' ? '#22c55e' :
                                  reg.status === 'pending' ? '#FFD700' :
                                  reg.status === 'rejected' ? '#ef4444' :
                                  '#9CA3AF',
                                border: 
                                  reg.status === 'approved' ? '1px solid rgba(34, 197, 94, 0.3)' :
                                  reg.status === 'pending' ? '1px solid rgba(255, 215, 0, 0.3)' :
                                  reg.status === 'rejected' ? '1px solid rgba(239, 68, 68, 0.3)' :
                                  '1px solid rgba(107, 114, 128, 0.3)'
                              }}>
                                {reg.status ? reg.status.toUpperCase() : 'UNKNOWN'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td style={{ 
                      padding: '1.25rem 1.5rem',
                      color: '#9CA3AF'
                    }}>
                      {studentInfo.schoolName}
                    </td>
                    <td style={{ 
                      padding: '1.25rem 1.5rem',
                      color: '#9CA3AF',
                      fontSize: '0.9rem'
                    }}>
                      {earliestDate ? new Date(earliestDate).toLocaleDateString() : 'Unknown'}
                    </td>
                    <td style={{ 
                      padding: '1.25rem 1.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleEditStudent(group)}
                          style={{
                            padding: '0.75rem 1.5rem',
                            borderRadius: '8px',
                            border: '1px solid #FFD700',
                            backgroundColor: 'transparent',
                            color: '#FFD700',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFD700';
                            e.currentTarget.style.color = '#0A0E17';
                            e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#FFD700';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          Edit Student
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '2rem',
          padding: '1.25rem 1.5rem',
          backgroundColor: '#111827',
          borderRadius: '16px',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 215, 0, 0.15)'
        }}>
          {/* Page Info */}
          <div style={{
            fontSize: '0.9rem',
            color: '#9CA3AF',
            fontWeight: '500'
          }}>
            Showing {startIndex + 1} to {Math.min(endIndex, studentRegistrations.length)} of {studentRegistrations.length} students
          </div>

          {/* Pagination Buttons */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center'
          }}>
            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-button"
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                border: currentPage === 1 ? '1px solid rgba(107, 114, 128, 0.3)' : '1px solid rgba(255, 215, 0, 0.3)',
                backgroundColor: currentPage === 1 ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                color: currentPage === 1 ? '#6B7280' : '#FFD700',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
              Previous
            </button>

            {/* Page Numbers */}
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`pagination-page-button ${currentPage === pageNum ? 'active' : ''}`}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    border: currentPage === pageNum ? '1px solid #FFD700' : '1px solid rgba(255, 215, 0, 0.2)',
                    backgroundColor: currentPage === pageNum ? '#FFD700' : 'transparent',
                    color: currentPage === pageNum ? '#0A0E17' : '#9CA3AF',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-button"
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                border: currentPage === totalPages ? '1px solid rgba(107, 114, 128, 0.3)' : '1px solid rgba(255, 215, 0, 0.3)',
                backgroundColor: currentPage === totalPages ? 'rgba(107, 114, 128, 0.1)' : 'transparent',
                color: currentPage === totalPages ? '#6B7280' : '#FFD700',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Next
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Student Details Edit Modal */}
      {showEditModal && selectedStudent && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '1rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeEditModal();
            }
          }}
        >
          <div style={{
            backgroundColor: '#1a1f2e',
            borderRadius: '20px',
            padding: '2rem',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 215, 0, 0.2)'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem',
              borderBottom: '1px solid rgba(255, 215, 0, 0.15)',
              paddingBottom: '1rem'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#FFD700',
                margin: 0,
                fontFamily: "'Bebas Neue', sans-serif",
                letterSpacing: '0.05em'
              }}>
                Student Details & Events
              </h2>
              <button
                onClick={closeEditModal}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
              >
                ×
              </button>
            </div>

            {/* Student Information */}
            <div style={{
              backgroundColor: '#0A0E17',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              border: '1px solid rgba(255, 215, 0, 0.15)'
            }}>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: '600',
                color: '#FFD700',
                marginBottom: '1rem'
              }}>
                Student Information
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.9rem', color: '#9CA3AF', fontWeight: '500' }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={editedFirstName}
                    onChange={(e) => setEditedFirstName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      marginTop: '0.25rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      backgroundColor: '#111827',
                      color: '#ffffff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FFD700'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255, 215, 0, 0.2)'}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.9rem', color: '#9CA3AF', fontWeight: '500' }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={editedLastName}
                    onChange={(e) => setEditedLastName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      marginTop: '0.25rem',
                      outline: 'none',
                      transition: 'border-color 0.2s ease',
                      backgroundColor: '#111827',
                      color: '#ffffff'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FFD700'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255, 215, 0, 0.2)'}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.9rem', color: '#9CA3AF', fontWeight: '500' }}>
                    Date of Birth
                  </label>
                  <div style={{
                    fontSize: '1rem',
                    color: '#ffffff',
                    marginTop: '0.25rem'
                  }}>
                    {selectedStudent.studentInfo.dateOfBirth ? 
                      new Date(selectedStudent.studentInfo.dateOfBirth).toLocaleDateString() : 'Unknown'}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '0.9rem', color: '#9CA3AF', fontWeight: '500' }}>
                    Gender
                  </label>
                  <div style={{
                    fontSize: '1rem',
                    color: '#ffffff',
                    marginTop: '0.25rem'
                  }}>
                    {selectedStudent.studentInfo.gender || 'Unknown'}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '0.9rem', color: '#9CA3AF', fontWeight: '500' }}>
                    School
                  </label>
                  <div style={{
                    fontSize: '1rem',
                    color: '#ffffff',
                    marginTop: '0.25rem'
                  }}>
                    {selectedStudent.studentInfo.schoolName}
                  </div>
                </div>
              </div>
            </div>

            {/* Events Section */}
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  color: '#FFD700',
                  margin: 0
                }}>
                  Registered Events ({selectedStudent.registrations.length})
                </h3>
                
                {/* Add Event Button for non-Grade 7 students with less than 3 events */}
                {(() => {
                  const isGrade7Student = selectedStudent.registrations.some((reg: any) => 
                    reg.ageGroup && reg.ageGroup.toLowerCase().includes('grade 7')
                  );
                  const hasLessThan3Events = selectedStudent.registrations.length < 3;
                  const availableEvents = !isGrade7Student ? getAvailableEvents(selectedStudent) : [];
                  
                  return (!isGrade7Student && hasLessThan3Events && availableEvents.length > 0) ? (
                    <button
                      onClick={() => setShowAddEventModal(true)}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        border: '1px solid #FFD700',
                        backgroundColor: '#FFD700',
                        color: '#0A0E17',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#FFC000';
                        e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#FFD700';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      + Add Event
                    </button>
                  ) : null;
                })()}
              </div>
              
              {/* Check if this is a Grade 7 student (all events are compulsory) */}
              {(() => {
                const isGrade7Student = selectedStudent.registrations.some((reg: any) => 
                  reg.ageGroup && reg.ageGroup.toLowerCase().includes('grade 7')
                );
                
                return (
                  <>
                    {isGrade7Student && selectedStudent.registrations.length > 0 && (
                      <div style={{
                        backgroundColor: 'rgba(255, 215, 0, 0.15)',
                        border: '1px solid rgba(255, 215, 0, 0.3)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#FFD700', marginBottom: '0.25rem' }}>
                            Grade 7 Student - All Events Compulsory
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#9CA3AF' }}>
                            Individual events cannot be removed. Use "Delete All Events" to remove this student completely.
                          </div>
                        </div>
                        <button
                          onClick={handleDeleteStudent}
                          disabled={isRemovingEvent}
                          style={{
                            padding: '0.75rem 1.5rem',
                            borderRadius: '8px',
                            border: '1px solid #dc2626',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: isRemovingEvent ? 'not-allowed' : 'pointer',
                            opacity: isRemovingEvent ? 0.6 : 1,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!isRemovingEvent) {
                              e.currentTarget.style.backgroundColor = '#b91c1c';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isRemovingEvent) {
                              e.currentTarget.style.backgroundColor = '#dc2626';
                            }
                          }}
                        >
                          {isRemovingEvent ? 'Deleting...' : 'Delete All Events'}
                        </button>
                      </div>
                    )}
              
              {selectedStudent.registrations.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: '#9CA3AF'
                }}>
                  No events registered
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {selectedStudent.registrations.map((reg: any) => (
                    <div 
                      key={reg.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1.5rem',
                        backgroundColor: reg.ageGroup && reg.ageGroup.toLowerCase().includes('grade 7') 
                          ? 'rgba(255, 215, 0, 0.1)' : '#0A0E17',
                        borderRadius: '12px',
                        border: reg.ageGroup && reg.ageGroup.toLowerCase().includes('grade 7') 
                          ? '1px solid rgba(255, 215, 0, 0.3)' : '1px solid rgba(255, 215, 0, 0.15)'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: '600',
                          color: '#ffffff',
                          fontSize: '1.1rem',
                          marginBottom: '0.5rem'
                        }}>
                          {reg.sport}
                        </div>
                        <div style={{
                          fontSize: '0.9rem',
                          color: '#9CA3AF',
                          marginBottom: '0.5rem'
                        }}>
                          Age Group: {reg.ageGroup}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.375rem 0.75rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: 
                              reg.status === 'approved' ? 'rgba(34, 197, 94, 0.2)' :
                              reg.status === 'pending' ? 'rgba(255, 215, 0, 0.2)' :
                              reg.status === 'rejected' ? 'rgba(239, 68, 68, 0.2)' :
                              'rgba(107, 114, 128, 0.2)',
                            color: 
                              reg.status === 'approved' ? '#22c55e' :
                              reg.status === 'pending' ? '#FFD700' :
                              reg.status === 'rejected' ? '#ef4444' :
                              '#9CA3AF',
                            border: 
                              reg.status === 'approved' ? '1px solid rgba(34, 197, 94, 0.3)' :
                              reg.status === 'pending' ? '1px solid rgba(255, 215, 0, 0.3)' :
                              reg.status === 'rejected' ? '1px solid rgba(239, 68, 68, 0.3)' :
                              '1px solid rgba(107, 114, 128, 0.3)'
                          }}>
                            {reg.status ? reg.status.toUpperCase() : 'UNKNOWN'}
                          </span>
                          {reg.date && (
                            <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                              Registered: {new Date(reg.date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Only show remove button for non-Grade 7 students */}
                      {!(reg.ageGroup && reg.ageGroup.toLowerCase().includes('grade 7')) && (
                        <button
                          onClick={() => handleRemoveEvent(reg.id)}
                          disabled={isRemovingEvent}
                          style={{
                            padding: '0.75rem 1rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(239, 68, 68, 0.5)',
                            backgroundColor: 'transparent',
                            color: '#ef4444',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: isRemovingEvent ? 'not-allowed' : 'pointer',
                            opacity: isRemovingEvent ? 0.6 : 1,
                            transition: 'all 0.3s ease',
                            marginLeft: '1rem'
                          }}
                        onMouseEnter={(e) => {
                          if (!isRemovingEvent) {
                            e.currentTarget.style.backgroundColor = '#ef4444';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.boxShadow = '0 0 15px rgba(239, 68, 68, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isRemovingEvent) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#ef4444';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {isRemovingEvent ? 'Removing...' : 'Remove'}
                      </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
                  </>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div style={{
              marginTop: '2rem',
              paddingTop: '1rem',
              borderTop: '1px solid rgba(255, 215, 0, 0.15)',
              textAlign: 'right',
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              {/* Save Name Button */}
              <button
                onClick={handleSaveStudentName}
                disabled={isUpdatingName || !editedFirstName.trim() || !editedLastName.trim()}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: isUpdatingName || !editedFirstName.trim() || !editedLastName.trim() 
                    ? '#4B5563' : '#22c55e',
                  color: isUpdatingName || !editedFirstName.trim() || !editedLastName.trim() 
                    ? '#9CA3AF' : '#0A0E17',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isUpdatingName || !editedFirstName.trim() || !editedLastName.trim() 
                    ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  if (!isUpdatingName && editedFirstName.trim() && editedLastName.trim()) {
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(34, 197, 94, 0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isUpdatingName && editedFirstName.trim() && editedLastName.trim()) {
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                {isUpdatingName ? 'Saving...' : 'Save Name'}
              </button>
              
              <button
                onClick={closeEditModal}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  backgroundColor: 'transparent',
                  color: '#FFD700',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showAddEventModal && selectedStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100
        }}>
          <div style={{
            backgroundColor: '#1a1f2e',
            borderRadius: '20px',
            padding: '2rem',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 215, 0, 0.2)'
          }}>
            {/* Modal Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#FFD700',
                margin: 0,
                fontFamily: "'Bebas Neue', sans-serif",
                letterSpacing: '0.05em'
              }}>
                Add Event for {selectedStudent.studentInfo.firstName} {selectedStudent.studentInfo.lastName}
              </h2>
              <button
                onClick={() => setShowAddEventModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
              >
                ×
              </button>
            </div>

            {/* Event Selection */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: '#FFD700',
                marginBottom: '1rem',
                display: 'block'
              }}>
                Select Event
              </label>
              
              {getAvailableEvents(selectedStudent).length === 0 ? (
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: 'rgba(255, 215, 0, 0.15)',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '8px',
                  color: '#FFD700',
                  fontSize: '0.9rem',
                  textAlign: 'center'
                }}>
                  No events available. All events for this student's gender/age group at their school are full (3 students max per event).
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1rem',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {getAvailableEvents(selectedStudent).map(eventName => {
                    const studentGender = selectedStudent.studentInfo.gender;
                    const studentAgeGroup = selectedStudent.registrations[0]?.ageGroup || 'Under 17';
                    const studentSchoolId = selectedStudent.studentInfo.schoolId;
                    
                    // Count existing registrations for this event
                    const existingCount = registrations.filter(reg => 
                      reg.sport === eventName &&
                      reg.age_group === studentAgeGroup &&
                      reg.gender === studentGender &&
                      reg.school_id === studentSchoolId &&
                      !reg.deleted_at
                    ).length;
                    
                    const displayName = eventName.charAt(0).toUpperCase() + eventName.slice(1).replace('_', ' ');
                    const isSelected = selectedEvent === eventName;
                    
                    return (
                      <div
                        key={eventName}
                        onClick={() => setSelectedEvent(eventName)}
                        style={{
                          padding: '1rem',
                          borderRadius: '12px',
                          border: `2px solid ${isSelected ? '#FFD700' : 'rgba(255, 215, 0, 0.2)'}`,
                          backgroundColor: isSelected ? 'rgba(255, 215, 0, 0.15)' : '#0A0E17',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          textAlign: 'center'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.5)';
                            e.currentTarget.style.backgroundColor = 'rgba(255, 215, 0, 0.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.2)';
                            e.currentTarget.style.backgroundColor = '#0A0E17';
                          }
                        }}
                      >
                        <div style={{
                          fontWeight: '600',
                          color: isSelected ? '#FFD700' : '#ffffff',
                          marginBottom: '0.5rem',
                          fontSize: '1rem'
                        }}>
                          {displayName}
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: isSelected ? '#FFD700' : '#9CA3AF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.25rem'
                        }}>
                          <span>{existingCount}/3 registered</span>
                          <div style={{
                            display: 'flex',
                            gap: '2px'
                          }}>
                            {[1, 2, 3].map(i => (
                              <div
                                key={i}
                                style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: i <= existingCount 
                                    ? (isSelected ? '#FFD700' : '#9CA3AF')
                                    : 'rgba(255, 215, 0, 0.2)'
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'flex-end' 
            }}>
              <button
                onClick={() => setShowAddEventModal(false)}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  backgroundColor: 'transparent',
                  color: '#FFD700',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddEvent}
                disabled={!selectedEvent || isAddingEvent}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: !selectedEvent || isAddingEvent ? '#4B5563' : '#FFD700',
                  color: !selectedEvent || isAddingEvent ? '#9CA3AF' : '#0A0E17',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: !selectedEvent || isAddingEvent ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {isAddingEvent ? 'Adding...' : 'Add Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration Form Modal */}
      {showRegistrationForm && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: '#1a1f2e',
            borderRadius: '20px',
            padding: '2rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 215, 0, 0.2)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#FFD700',
                margin: 0,
                fontFamily: "'Bebas Neue', sans-serif",
                letterSpacing: '0.05em'
              }}>
                Register New Student
              </h3>
              <button
                onClick={() => setShowRegistrationForm(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleRegistrationSubmit}>
              {/* Basic Information */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#9CA3AF'
                  }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={registrationForm.firstName}
                    onChange={(e) => handleRegistrationFormChange('firstName', e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: '#111827',
                      color: '#ffffff'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#9CA3AF'
                  }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={registrationForm.lastName}
                    onChange={(e) => handleRegistrationFormChange('lastName', e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: '#111827',
                      color: '#ffffff'
                    }}
                  />
                </div>
              </div>

              {/* Date of Birth and Gender */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#9CA3AF'
                  }}>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={registrationForm.dateOfBirth}
                    onChange={(e) => handleRegistrationFormChange('dateOfBirth', e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: '#111827',
                      color: '#ffffff'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#9CA3AF'
                  }}>
                    Gender
                  </label>
                  <select
                    value={registrationForm.gender}
                    onChange={(e) => handleRegistrationFormChange('gender', e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: '#111827',
                      color: '#ffffff'
                    }}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>

              {/* Age Group Selection for 13-year-olds */}
              {registrationForm.dateOfBirth && is13YearsOld(registrationForm.dateOfBirth) && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#FFD700'
                  }}>
                    This student is 13 years old - Please select age group: *
                  </label>
                  <select
                    value={registrationForm.ageGroup || ''}
                    onChange={(e) => handleRegistrationFormChange('ageGroup', e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #FFD700',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: 'rgba(255, 215, 0, 0.15)',
                      color: '#ffffff'
                    }}
                  >
                    <option value="">Select Age Group...</option>
                    <option value="Grade 7">Grade 7</option>
                    <option value="Under 15">Under 15</option>
                  </select>
                  {registrationForm.ageGroup && (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      fontSize: '0.875rem', 
                      color: '#22c55e',
                      fontWeight: '500'
                    }}>
                      ✓ Selected: {registrationForm.ageGroup}
                    </div>
                  )}
                </div>
              )}

              {/* School Selection - Searchable Dropdown */}
              <div style={{ marginBottom: '1rem', position: 'relative' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: '#9CA3AF'
                }}>
                  School
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    ref={schoolInputRef}
                    type="text"
                    value={showSchoolDropdown ? registrationSchoolSearch : selectedSchoolName}
                    onChange={(e) => {
                      setRegistrationSchoolSearch(e.target.value);
                      if (!showSchoolDropdown) setShowSchoolDropdown(true);
                    }}
                    onFocus={() => {
                      setShowSchoolDropdown(true);
                      setRegistrationSchoolSearch('');
                    }}
                    placeholder="Search and select school..."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      paddingRight: '2.5rem',
                      border: '1px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: '#111827',
                      color: '#ffffff'
                    }}
                  />
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="#9CA3AF" 
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
                
                {/* Dropdown List - Using Portal */}
                {showSchoolDropdown && ReactDOM.createPortal(
                  <>
                    {/* Click outside overlay */}
                    <div 
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 10000
                      }}
                      onClick={() => setShowSchoolDropdown(false)}
                    />
                    {/* Dropdown */}
                    <div 
                      style={{
                        position: 'fixed',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid rgba(255, 215, 0, 0.3)',
                        borderRadius: '8px',
                        zIndex: 10001,
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
                      }}
                    >
                      {filteredSchoolsForRegistration.length === 0 ? (
                        <div style={{
                          padding: '0.75rem 1rem',
                          color: '#9CA3AF',
                          fontSize: '0.9rem',
                          textAlign: 'center'
                        }}>
                          No schools found
                        </div>
                      ) : (
                        filteredSchoolsForRegistration.map(school => (
                          <div
                            key={school.id}
                            onClick={() => {
                              handleRegistrationFormChange('schoolId', school.id);
                              setRegistrationSchoolSearch('');
                              setShowSchoolDropdown(false);
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
                              transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 215, 0, 0.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <span style={{ color: '#ffffff', fontSize: '0.9rem' }}>{school.name}</span>
                            <span style={{
                              fontSize: '0.7rem',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              backgroundColor: school.source === 'App' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                              color: school.source === 'App' ? '#10b981' : '#3b82f6',
                              fontWeight: '600'
                            }}>
                              {school.source}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </>,
                  document.body
                )}
              </div>

              {/* Event Selection */}
              {registrationForm.dateOfBirth && registrationForm.schoolId && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#FFD700'
                  }}>
                    Select Events ({registrationForm.sports.length} selected)
                  </label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '0.5rem',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '8px',
                    padding: '1rem',
                    backgroundColor: '#0A0E17'
                  }}>
                    {events.map(event => (
                      <label
                        key={event.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          color: '#ffffff'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={registrationForm.sports.includes(event.id)}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setRegistrationForm(prev => ({
                              ...prev,
                              sports: isChecked 
                                ? [...prev.sports, event.id]
                                : prev.sports.filter(id => id !== event.id)
                            }));
                          }}
                          style={{
                            width: '16px',
                            height: '16px'
                          }}
                        />
                        {event.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Form Actions */}
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowRegistrationForm(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    borderRadius: '8px',
                    backgroundColor: 'transparent',
                    color: '#FFD700',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingRegistration}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: isSubmittingRegistration ? '#4B5563' : '#FFD700',
                    color: isSubmittingRegistration ? '#9CA3AF' : '#0A0E17',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    cursor: isSubmittingRegistration ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {isSubmittingRegistration ? 'Registering...' : 'Register Student'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          maxWidth: '400px',
          backgroundColor: toastType === 'success' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
          border: `1px solid ${toastType === 'success' ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
          color: toastType === 'success' ? '#22c55e' : '#ef4444',
          fontWeight: '600',
          backdropFilter: 'blur(10px)'
        }}>
          {toastMessage}
        </div>
      )}
    </div>
  );
};

// Admin School Management Component
interface AdminSchoolManagementProps {
  smsSchools: School[];
  appSchools: AppSchool[];
  regions: Region[];
  schoolLevels: SchoolLevel[];
  currentUser: any;
  onRefresh: () => void;
}

const AdminSchoolManagement: React.FC<AdminSchoolManagementProps> = ({ smsSchools, appSchools, regions, schoolLevels, currentUser, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSchoolTab, setActiveSchoolTab] = useState<'sms' | 'app'>('sms');
  const [showAddSchoolModal, setShowAddSchoolModal] = useState(false);
  const [newSchoolName, setNewSchoolName] = useState('');
  const [newSchoolRegionId, setNewSchoolRegionId] = useState('');
  const [newSchoolLevelId, setNewSchoolLevelId] = useState('');
  const [isAddingSchool, setIsAddingSchool] = useState(false);
  const [isDeletingSchool, setIsDeletingSchool] = useState<string | null>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState<AppSchool | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // State for existing school search in Add School modal
  const [showExistingSchoolsDropdown, setShowExistingSchoolsDropdown] = useState(false);
  const [existingSchoolDropdownPosition, setExistingSchoolDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const schoolNameInputRef = React.useRef<HTMLInputElement>(null);
  
  // Normalize school name for comparison (case-insensitive, punctuation-insensitive)
  const normalizeSchoolName = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ')    // Normalize multiple spaces to single space
      .trim();
  };
  
  // Combine all schools for duplicate checking
  const allExistingSchools = React.useMemo(() => {
    const sms = smsSchools.map(s => ({ id: s.id, name: s.name, normalized: normalizeSchoolName(s.name), source: 'SMS' as const }));
    const app = appSchools.map(s => ({ id: s.id, name: s.name, normalized: normalizeSchoolName(s.name), source: 'App' as const }));
    return [...sms, ...app].sort((a, b) => a.name.localeCompare(b.name));
  }, [smsSchools, appSchools]);
  
  // Filter existing schools based on typed name (case & punctuation insensitive)
  const matchingExistingSchools = React.useMemo(() => {
    if (!newSchoolName.trim() || newSchoolName.length < 2) return [];
    const normalizedSearch = normalizeSchoolName(newSchoolName);
    return allExistingSchools.filter(s => 
      s.normalized.includes(normalizedSearch)
    ).slice(0, 10);
  }, [allExistingSchools, newSchoolName]);
  
  // Check if exact match exists (case & punctuation insensitive)
  const exactMatchExists = React.useMemo(() => {
    const normalizedInput = normalizeSchoolName(newSchoolName);
    if (!normalizedInput) return false;
    return allExistingSchools.some(s => s.normalized === normalizedInput);
  }, [allExistingSchools, newSchoolName]);
  
  // Update dropdown position
  React.useEffect(() => {
    if (showExistingSchoolsDropdown && schoolNameInputRef.current) {
      const rect = schoolNameInputRef.current.getBoundingClientRect();
      setExistingSchoolDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, [showExistingSchoolsDropdown, newSchoolName]);

  const showNotification = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // Filter schools based on search
  const filteredSmsSchools = smsSchools.filter(school =>
    school.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAppSchools = appSchools.filter(school =>
    school.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentSchools = activeSchoolTab === 'sms' ? filteredSmsSchools : filteredAppSchools;
  const totalPages = Math.ceil(currentSchools.length / itemsPerPage);
  const paginatedSchools = currentSchools.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when switching tabs or searching
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSchoolTab, searchTerm]);

  const handleAddSchool = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newSchoolName.trim()) {
      showNotification('Please enter a school name', 'error');
      return;
    }

    if (!newSchoolRegionId) {
      showNotification('Please select a region', 'error');
      return;
    }

    if (!newSchoolLevelId) {
      showNotification('Please select a school level', 'error');
      return;
    }
    
    // Warn if exact match exists
    if (exactMatchExists) {
      showNotification('A school with this exact name already exists. Please use a different name.', 'error');
      return;
    }

    try {
      setIsAddingSchool(true);
      await dataService.addAppSchool({
        name: newSchoolName.trim(),
        region_id: newSchoolRegionId,
        school_level_id: newSchoolLevelId,
        created_by: currentUser.id
      });
      showNotification('School added successfully!', 'success');
      setNewSchoolName('');
      setNewSchoolRegionId('');
      setNewSchoolLevelId('');
      setShowExistingSchoolsDropdown(false);
      setShowAddSchoolModal(false);
      onRefresh();
    } catch (error) {
      console.error('Error adding school:', error);
      showNotification('Failed to add school. Please try again.', 'error');
    } finally {
      setIsAddingSchool(false);
    }
  };

  const handleDeleteSchool = async (schoolId: string) => {
    try {
      setIsDeletingSchool(schoolId);
      await dataService.deleteAppSchool(schoolId, currentUser.id);
      showNotification('School deleted successfully!', 'success');
      setShowDeleteConfirmModal(false);
      setSchoolToDelete(null);
      onRefresh();
    } catch (error) {
      console.error('Error deleting school:', error);
      showNotification('Failed to delete school. Please try again.', 'error');
    } finally {
      setIsDeletingSchool(null);
    }
  };

  const openDeleteConfirmation = (school: AppSchool) => {
    setSchoolToDelete(school);
    setShowDeleteConfirmModal(true);
  };

  const closeDeleteConfirmation = () => {
    setSchoolToDelete(null);
    setShowDeleteConfirmModal(false);
  };

  return (
    <div style={{ padding: '2rem 1.5rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h2 style={{
          fontSize: '1.8rem',
          fontWeight: '600',
          color: '#FFD700',
          margin: 0,
          letterSpacing: '-0.025em'
        }}>
          School Management
        </h2>
        <button
          onClick={() => setShowAddSchoolModal(true)}
          style={{
            background: 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
            color: '#0A0E17',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '12px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add New School
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search schools..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: '1',
            minWidth: '250px',
            padding: '0.75rem 1rem',
            border: '2px solid rgba(255, 215, 0, 0.2)',
            borderRadius: '12px',
            fontSize: '0.95rem',
            transition: 'border-color 0.2s ease',
            background: '#111827',
            color: '#ffffff'
          }}
        />
        
        <button
          onClick={() => setActiveSchoolTab('sms')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeSchoolTab === 'sms' ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)' : '#111827',
            color: activeSchoolTab === 'sms' ? '#3b82f6' : '#9CA3AF',
            border: activeSchoolTab === 'sms' ? '2px solid rgba(59, 130, 246, 0.4)' : '2px solid rgba(255, 215, 0, 0.2)',
            borderRadius: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          SMS Schools ({smsSchools.length})
        </button>
        
        <button
          onClick={() => setActiveSchoolTab('app')}
          style={{
            padding: '0.75rem 1.5rem',
            background: activeSchoolTab === 'app' ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)' : '#111827',
            color: activeSchoolTab === 'app' ? '#10b981' : '#9CA3AF',
            border: activeSchoolTab === 'app' ? '2px solid rgba(16, 185, 129, 0.4)' : '2px solid rgba(255, 215, 0, 0.2)',
            borderRadius: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          App Schools ({appSchools.length})
        </button>
      </div>

      {/* Schools Table */}
      <div style={{
        overflowX: 'auto',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 215, 0, 0.15)',
        background: 'rgba(17, 24, 39, 0.4)'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'transparent' }}>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)' }}>
              <th style={{ 
                padding: '1.5rem 1.5rem', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: '#FFD700',
                fontSize: '0.95rem',
                borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
              }}>School Name</th>
              {activeSchoolTab === 'sms' && (
                <>
                  <th style={{ 
                    padding: '1.5rem 1.5rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#FFD700',
                    fontSize: '0.95rem',
                    borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                  }}>Region</th>
                  <th style={{ 
                    padding: '1.5rem 1.5rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#FFD700',
                    fontSize: '0.95rem',
                    borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                  }}>School Level</th>
                </>
              )}
              {activeSchoolTab === 'app' && (
                <>
                  <th style={{ 
                    padding: '1.5rem 1.5rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#FFD700',
                    fontSize: '0.95rem',
                    borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                  }}>Region</th>
                  <th style={{ 
                    padding: '1.5rem 1.5rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#FFD700',
                    fontSize: '0.95rem',
                    borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                  }}>School Level</th>
                  <th style={{ 
                    padding: '1.5rem 1.5rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#FFD700',
                    fontSize: '0.95rem',
                    borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                  }}>Date Added</th>
                  <th style={{ 
                    padding: '1.5rem 1.5rem', 
                    textAlign: 'center', 
                    fontWeight: '600', 
                    color: '#FFD700',
                    fontSize: '0.95rem',
                    borderBottom: '1px solid rgba(255, 215, 0, 0.2)'
                  }}>Actions</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedSchools.length === 0 ? (
              <tr>
                <td 
                  colSpan={activeSchoolTab === 'app' ? 5 : 3} 
                  style={{ 
                    padding: '3rem', 
                    textAlign: 'center', 
                    color: '#9CA3AF',
                    fontSize: '1rem'
                  }}
                >
                  No schools found
                </td>
              </tr>
            ) : (
              paginatedSchools.map((school, index) => (
                <tr
                  key={school.id}
                  style={{
                    background: index % 2 === 0 ? 'transparent' : 'rgba(17, 24, 39, 0.3)',
                    borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 215, 0, 0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'transparent' : 'rgba(17, 24, 39, 0.3)'}
                >
                  <td style={{ 
                    padding: '1.25rem 1.5rem', 
                    color: '#ffffff',
                    fontWeight: '500',
                    fontSize: '0.95rem'
                  }}>
                    {school.name}
                  </td>
                  {activeSchoolTab === 'sms' && (
                    <>
                      <td style={{ 
                        padding: '1.25rem 1.5rem', 
                        color: '#9CA3AF',
                        fontSize: '0.9rem'
                      }}>
                        {(school as School).region_name || '—'}
                      </td>
                      <td style={{ 
                        padding: '1.25rem 1.5rem', 
                        color: '#9CA3AF',
                        fontSize: '0.9rem'
                      }}>
                        {(school as School).school_level_name || '—'}
                      </td>
                    </>
                  )}
                  {activeSchoolTab === 'app' && (
                    <>
                      <td style={{ 
                        padding: '1.25rem 1.5rem', 
                        color: '#9CA3AF',
                        fontSize: '0.9rem'
                      }}>
                        {regions.find(r => r.id === (school as AppSchool).region_id)?.name || '—'}
                      </td>
                      <td style={{ 
                        padding: '1.25rem 1.5rem', 
                        color: '#9CA3AF',
                        fontSize: '0.9rem'
                      }}>
                        {schoolLevels.find(l => l.id === (school as AppSchool).school_level_id)?.name || '—'}
                      </td>
                      <td style={{ 
                        padding: '1.25rem 1.5rem', 
                        color: '#9CA3AF', 
                        fontSize: '0.9rem' 
                      }}>
                        {new Date((school as AppSchool).created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </td>
                      <td style={{ padding: '1.25rem 1.5rem', textAlign: 'center' }}>
                        <button
                          onClick={() => openDeleteConfirmation(school as AppSchool)}
                          disabled={isDeletingSchool === school.id}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'linear-gradient(135deg, rgba(196, 30, 58, 0.15) 0%, rgba(196, 30, 58, 0.1) 100%)',
                            color: '#C41E3A',
                            border: '1px solid rgba(196, 30, 58, 0.3)',
                            borderRadius: '8px',
                            cursor: isDeletingSchool === school.id ? 'not-allowed' : 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            opacity: isDeletingSchool === school.id ? 0.5 : 1,
                            transition: 'all 0.2s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3,6 5,6 21,6"/>
                            <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                          </svg>
                          {isDeletingSchool === school.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: 'rgba(17, 24, 39, 0.4)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 215, 0, 0.1)'
        }}>
          <span style={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, currentSchools.length)} of {currentSchools.length} schools
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.6rem 1.2rem',
                background: currentPage === 1 ? 'rgba(75, 85, 99, 0.2)' : 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.1) 100%)',
                color: currentPage === 1 ? '#6B7280' : '#FFD700',
                border: currentPage === 1 ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '8px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              Previous
            </button>
            <span style={{ 
              color: '#FFD700', 
              fontSize: '0.9rem',
              padding: '0.6rem 1rem',
              background: 'rgba(255, 215, 0, 0.1)',
              borderRadius: '8px',
              fontWeight: '600'
            }}>
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.6rem 1.2rem',
                background: currentPage === totalPages ? 'rgba(75, 85, 99, 0.2)' : 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.1) 100%)',
                color: currentPage === totalPages ? '#6B7280' : '#FFD700',
                border: currentPage === totalPages ? '1px solid rgba(75, 85, 99, 0.3)' : '1px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '8px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add School Modal */}
      {showAddSchoolModal && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1f2e 0%, #111827 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            padding: '2rem',
            width: '100%',
            maxWidth: '500px',
            margin: '1rem',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 215, 0, 0.1)'
          }}>
            <h3 style={{
              margin: '0 0 1.5rem 0',
              color: '#FFD700',
              fontSize: '1.5rem',
              fontWeight: '600',
              letterSpacing: '-0.025em'
            }}>
              Add New School
            </h3>
            <form onSubmit={handleAddSchool}>
              <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}>
                  School Name *
                </label>
                <input
                  ref={schoolNameInputRef}
                  type="text"
                  value={newSchoolName}
                  onChange={(e) => {
                    setNewSchoolName(e.target.value);
                    if (e.target.value.length >= 2) {
                      setShowExistingSchoolsDropdown(true);
                    } else {
                      setShowExistingSchoolsDropdown(false);
                    }
                  }}
                  onFocus={() => {
                    if (newSchoolName.length >= 2) {
                      setShowExistingSchoolsDropdown(true);
                    }
                  }}
                  placeholder="Start typing to search existing schools..."
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#111827',
                    border: exactMatchExists ? '2px solid rgba(239, 68, 68, 0.5)' : '2px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '12px',
                    color: '#fff',
                    fontSize: '0.95rem',
                    transition: 'border-color 0.2s ease'
                  }}
                  autoFocus
                />
                
                {/* Existing schools dropdown - using portal */}
                {showExistingSchoolsDropdown && matchingExistingSchools.length > 0 && ReactDOM.createPortal(
                  <>
                    <div 
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 10000
                      }}
                      onClick={() => setShowExistingSchoolsDropdown(false)}
                    />
                    <div style={{
                      position: 'fixed',
                      top: existingSchoolDropdownPosition.top,
                      left: existingSchoolDropdownPosition.left,
                      width: existingSchoolDropdownPosition.width,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid rgba(255, 215, 0, 0.3)',
                      borderRadius: '8px',
                      zIndex: 10001,
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
                    }}>
                      <div style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
                        color: '#f87171',
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}>
                        ⚠️ Similar schools already exist:
                      </div>
                      {matchingExistingSchools.map(school => (
                        <div
                          key={school.id}
                          style={{
                            padding: '0.75rem 1rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(255, 215, 0, 0.1)',
                            backgroundColor: school.name.toLowerCase() === newSchoolName.toLowerCase().trim() 
                              ? 'rgba(239, 68, 68, 0.15)' 
                              : 'transparent'
                          }}
                        >
                          <span style={{ 
                            color: '#ffffff', 
                            fontSize: '0.9rem',
                            fontWeight: school.name.toLowerCase() === newSchoolName.toLowerCase().trim() ? '600' : '400'
                          }}>
                            {school.name}
                          </span>
                          <span style={{
                            fontSize: '0.7rem',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            backgroundColor: school.source === 'App' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                            color: school.source === 'App' ? '#10b981' : '#3b82f6',
                            fontWeight: '600'
                          }}>
                            {school.source}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>,
                  document.body
                )}
                
                {/* Warning message for exact match */}
                {exactMatchExists && (
                  <p style={{
                    margin: '0.5rem 0 0 0',
                    color: '#f87171',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    This school name already exists in the system
                  </p>
                )}
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}>
                  Region *
                </label>
                <select
                  value={newSchoolRegionId}
                  onChange={(e) => setNewSchoolRegionId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#111827',
                    border: '2px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '12px',
                    color: '#fff',
                    fontSize: '0.95rem',
                    transition: 'border-color 0.2s ease',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">Select a region</option>
                  {regions.map(region => (
                    <option key={region.id} value={region.id}>{region.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  color: '#ffffff',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}>
                  School Level *
                </label>
                <select
                  value={newSchoolLevelId}
                  onChange={(e) => setNewSchoolLevelId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#111827',
                    border: '2px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '12px',
                    color: '#fff',
                    fontSize: '0.95rem',
                    transition: 'border-color 0.2s ease',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">Select a school level</option>
                  {schoolLevels.map(level => (
                    <option key={level.id} value={level.id}>{level.name}</option>
                  ))}
                </select>
              </div>
              <p style={{
                margin: '0 0 1.5rem 0',
                padding: '1rem',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                color: '#93c5fd',
                fontSize: '0.9rem'
              }}>
                <strong>Note:</strong> This school will be added to the App Schools database and will be available for registration.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddSchoolModal(false);
                    setNewSchoolName('');
                    setNewSchoolRegionId('');
                    setNewSchoolLevelId('');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'transparent',
                    color: '#9CA3AF',
                    border: '2px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isAddingSchool}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: isAddingSchool ? 'rgba(75, 85, 99, 0.5)' : 'linear-gradient(135deg, #FFD700 0%, #FFC107 100%)',
                    color: isAddingSchool ? '#9CA3AF' : '#0A0E17',
                    border: 'none',
                    borderRadius: '12px',
                    fontWeight: '600',
                    cursor: isAddingSchool ? 'not-allowed' : 'pointer',
                    fontSize: '0.95rem',
                    boxShadow: isAddingSchool ? 'none' : '0 4px 12px rgba(255, 215, 0, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isAddingSchool ? 'Adding...' : 'Add School'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && schoolToDelete && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1f2e 0%, #111827 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(196, 30, 58, 0.3)',
            padding: '2rem',
            width: '100%',
            maxWidth: '450px',
            margin: '1rem',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(196, 30, 58, 0.1)'
          }}>
            {/* Warning Icon */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(196, 30, 58, 0.2) 0%, rgba(196, 30, 58, 0.1) 100%)',
                border: '2px solid rgba(196, 30, 58, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18"/>
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
              </div>
            </div>

            <h3 style={{
              margin: '0 0 0.75rem 0',
              color: '#C41E3A',
              fontSize: '1.5rem',
              fontWeight: '600',
              letterSpacing: '-0.025em',
              textAlign: 'center'
            }}>
              Delete School
            </h3>

            <p style={{
              margin: '0 0 1.5rem 0',
              color: '#9CA3AF',
              fontSize: '1rem',
              textAlign: 'center',
              lineHeight: '1.6'
            }}>
              Are you sure you want to delete <strong style={{ color: '#ffffff' }}>{schoolToDelete.name}</strong>?
            </p>

            <div style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, rgba(196, 30, 58, 0.1) 0%, rgba(196, 30, 58, 0.05) 100%)',
              border: '1px solid rgba(196, 30, 58, 0.2)',
              borderRadius: '12px',
              marginBottom: '1.5rem'
            }}>
              <p style={{
                margin: 0,
                color: '#f87171',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginTop: '2px', flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>This action will remove the school from the active list. The school will no longer be available for registration.</span>
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={closeDeleteConfirmation}
                disabled={isDeletingSchool !== null}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'transparent',
                  color: '#9CA3AF',
                  border: '2px solid rgba(255, 215, 0, 0.2)',
                  borderRadius: '12px',
                  cursor: isDeletingSchool !== null ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  opacity: isDeletingSchool !== null ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteSchool(schoolToDelete.id)}
                disabled={isDeletingSchool !== null}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: isDeletingSchool !== null ? 'rgba(75, 85, 99, 0.5)' : 'linear-gradient(135deg, #C41E3A 0%, #a01830 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '600',
                  cursor: isDeletingSchool !== null ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  boxShadow: isDeletingSchool !== null ? 'none' : '0 4px 12px rgba(196, 30, 58, 0.3)',
                  transition: 'all 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3,6 5,6 21,6"/>
                  <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                </svg>
                {isDeletingSchool !== null ? 'Deleting...' : 'Delete School'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
          zIndex: 1001,
          maxWidth: '400px',
          backgroundColor: toastType === 'success' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
          border: `1px solid ${toastType === 'success' ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
          color: toastType === 'success' ? '#22c55e' : '#ef4444',
          fontWeight: '600',
          backdropFilter: 'blur(10px)'
        }}>
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;